/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { BAR_CHART_TYPES, CARD_SIZES, CARD_TYPES } from 'carbon-addons-iot-react';
import capitalize from 'lodash/capitalize';
import every from 'lodash/every';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import some from 'lodash/some';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import countBy from 'lodash/countBy';
import isNaN from 'lodash/isNaN';
import React from 'react';
import { defineMessages } from 'react-intl';

import {
  COLUMNTYPE_NUMBER,
  DASHBOARD_DATA_TYPES,
  DB2_IDENTIFIER_MAXLENGTH,
  POSTGRESQL_IDENTIFIER_MAXLENGTH,
} from '../../Common/constants/constants';
import {
  determineDataSourceBasedOnSelectedTimeRange,
  determineValidTimeRangesForCard,
  isTimeBasedCard,
} from '../../Common/utils/cardUtilityFunctions';
import AlertCardEditContent from '../components/DashboardTemplateEditor/AlertCard/AlertCardEditContent';
import AlertCardEditSettings from '../components/DashboardTemplateEditor/AlertCard/AlertCardEditSettings';
import TimeSeriesCardEditContent from '../components/DashboardTemplateEditor/TimeSeriesCard/TimeSeriesCardEditContent';
import {
  CUSTOM_CARD_TYPES,
  TIME_FREQUENCY_TIME_GRAIN_MAP,
  TIME_GRAINS,
} from '../constants/MonitoringConstants';
import {
  COLUMNTYPE_BOOLEAN,
  DATAITEM_TAG_ALERT,
  DATAITEM_TAG_COMPLEX,
  DATAITEM_TYPE_CALCULATED_METRIC,
  DATAITEM_TYPE_DIMENSION,
  DATAITEM_TYPE_METRIC,
  RESOURCE_TYPES,
  COLUMNTYPE_TIMESTAMP,
  COLUMNTYPE_LITERAL,
  DATAITEM_TYPE_DERIVED,
} from '../../Setup/constants/constants';
import {
  AggregationInfo,
  AggregationMethods,
  AttributeMap,
  Card,
  CardAttributes,
  CardOnChangeType,
  CardTypes,
  DashboardTemplate,
  DashboardTemplateType,
  DataSourceAttribute,
  HotspotRenderType,
  ImageType,
  RenderEditContentReturnType,
  SummaryDto,
  Threshold,
} from '../types/dashboardTypes';
import CustomTableCardEditContent from '../components/CustomTableCardEditContent/CustomTableCardEditContent';
import AlertsTable from '../../Common/components/AlertsTable/AlertsTable';
import ThirdPartyCardEditContent from '../components/DashboardTemplateEditor/ThirdPartyCard/ThirdPartyCardEditContent';
import ThirdPartyCardContainer from '../containers/ThirdPartyCardContainer/ThirdPartyCardContainer';

import {
  getAlertCardDefaultColumsState,
  validateCardAttributesWithGroupBy,
} from './dashboardUtils';

import { validateNoSpaces } from 'dataFormFunctions';
import {
  formatDataItemName,
  isDataItemAlert,
  isDataItemCategoryAlert,
  isDataItemCategoryTransformer,
  isDataItemDerived,
  isDataItemDimension,
  isDataItemMetric,
  isDataItemNumeric,
  isDataItemRawMetric,
  isDataItemStreamingMetric,
} from 'dataFormatterFunctions';
import { validateMaxLength, validateSpecialCharacters, validateUniqueName } from 'formFunctions';
import { getLocalStorageIsDB2 } from 'localStorageUtils';
import { FormatMessageType, Version } from 'CommonTypes';
import {
  DataItemType,
  DataItemTypeWithDestination,
  EntityTypesType,
  V1DimensionsType,
} from 'EntitiesTypes';
import { KPIFunctionDtoType } from 'KPITypes';
import {
  filterAttributesToMap,
  hasRawMetric,
  hasV1DerivedMetric,
  hasV2DerivedMetric,
} from 'dashboardFetcher';

const messages = defineMessages({
  dashboardEmpty:
    'The dashboard does not contain any cards. You must add atleast one card before saving the dashboard.',
  columns: 'Columns',
  alertSectionTooltip: 'Markup the line chart with additional alert data',
  alerts: 'Alerts',
  alertCardContentTooltip:
    'Show or hide columns. You can show alert fields and dimensions as columns',
  sum: 'Sum',
  last: 'Last',
  min: 'Minimum',
  mean: 'Mean',
  max: 'Maximum',
  first: 'First',
  count: 'Count',
  std: 'Standard deviation',
  none: 'None',
  errorV1AndV2DataItems: 'Batch metric cannot be shown with streaming metric',
  cardErrorMessage: 'Card with title {title} and id {id} has error: {errorMessage}',
});

/**
 * Validates that a dashboard has some cards
 * Validateds that dashboard contains cards with groupBy specific conditions
 */
export const validateDashboard = (
  dashboardMetadataJson: DashboardTemplate,
  formatMessage: FormatMessageType,
  hierarchyType: string,
  dataItems: Array<DataItemType>,
  hasStreamingMetricEnabled = false
): string | null => {
  if (!dashboardMetadataJson || isEmpty(dashboardMetadataJson?.cards)) {
    return formatMessage(messages.dashboardEmpty);
  }

  const isHierarchyDashboard =
    hierarchyType !== RESOURCE_TYPES.DEVICE && hierarchyType !== RESOURCE_TYPES.DEVICE_TYPE;

  // Should only check groupBy conditon for V2 deviceTypes or device
  if (!isHierarchyDashboard && hasStreamingMetricEnabled) {
    const errorMessages = [];

    dashboardMetadataJson.cards.forEach(card => {
      const { id, title } = card;

      // otherwise we're showing metric data
      const attributesToMap = filterAttributesToMap(card.dataSource);

      // if the card does not contains any attributes or dataitems then return
      if (isEmpty(attributesToMap)) {
        return;
      }
      // To check if the attributes contains V1 derived(batch) metric
      const hasV1DerivedMetrics = hasV1DerivedMetric(attributesToMap, dataItems);
      // To check if the attributes contains V2 derived(steaming) metric
      const hasV2DerivedMetrics = hasV2DerivedMetric(attributesToMap, dataItems);

      const hasV1DerivedMetricAndRawMetrics =
        hasV1DerivedMetrics && hasRawMetric(attributesToMap, dataItems);

      // Not to call v2 datalake api if there is only V1 batch metric or V1 batch + raw metric
      const isV2DatalakeApiCall = !hasV1DerivedMetrics && !hasV1DerivedMetricAndRawMetrics;

      // Throw error if it conatins batch and streaming data items together
      if (hasV1DerivedMetrics && hasV2DerivedMetrics) {
        errorMessages.push(formatMessage(messages.errorV1AndV2DataItems));
      } else if (isV2DatalakeApiCall) {
        const errorMessage = validateCardAttributesWithGroupBy(card, dataItems, formatMessage);
        if (errorMessage) {
          errorMessages.push(formatMessage(messages.cardErrorMessage, { title, id, errorMessage }));
        }
      }
    });

    return errorMessages.length >= 1 ? errorMessages.join(',') : null;
  }

  return null;
};

/**
 * Map the selected granularity to a language the dashboard understands
 * @param {string} granularityTimeFrequency
 * @returns {string}
 */
export const mapTimeFrequencyToTimeGrain = (granularityTimeFrequency: string): string =>
  TIME_FREQUENCY_TIME_GRAIN_MAP[granularityTimeFrequency];

/**
 * Map the summary aggregation info to dashboard data items
 * @param {Array<Object>} aggregationInfoList
 * @param {Array<Object>} dataItems
 * @returns {Array<Object>}
 */
export const mergeAggregationInfoListAndDataItems = (
  aggregationInfoList: AggregationInfo[] = [],
  dataItems: DataItemType[] = []
): Array<{ name: string; columnType: string; type: string; aggregMethods: AggregationMethods }> =>
  aggregationInfoList
    .map(i => {
      const di = dataItems.find(j => j.name === i.inputDataItem);
      return di
        ? {
            name: di.name,
            columnType: di.columnType,
            type: di.type,
            aggregMethods: i.aggregMethods,
          }
        : null;
    })
    .filter(i => i); // filter out all null

/**
 * Collects the available dimension filters for a ValueCard in the editor
 * @param {object} summary current summary dashboard data
 * @returns {object} object of available dimensions where the key is the dimension and the
 * value is a list of dimension values to filter on (ex: { manufacturer: ['rentech', 'GHI Industries'] })
 */
export const getAvailableDimensions = (summary: SummaryDto): V1DimensionsType => {
  const { dimensionNames, summaryFilters } = summary;
  const availableDimensions = {};

  dimensionNames?.forEach(dimensionName => {
    let dimensionValues = [];

    if (summaryFilters) {
      dimensionValues = summaryFilters[dimensionName] ?? [];
    } // if this is a new dashboard, summaryFilters isn't available yet, so make dummy sample names for now
    else {
      dimensionValues.push(`Sample ${dimensionName} 1`, `Sample ${dimensionName} 2`);
    }

    availableDimensions[dimensionName] = uniq(dimensionValues.filter(i => i)).map(
      value => (typeof value === 'number' ? value.toString() : value) // these values need to be strings for the chart labels
    );
  });

  return availableDimensions;
};

/**
 *  Formats the dataItem labels to display in the dataSeries section of the CardEditor
 * @param {string} dataItem dataItem name to be formatted
 * @param {array} aggregationInfoList list of aggregated dataItems that are being
 * added to the dashboard
 */
export const formatDataItemLabel = (
  dataItem: string,
  aggregationInfoList: AggregationInfo[],
  userDefinedAttribute: CardAttributes
): string => {
  if (userDefinedAttribute?.label) {
    return userDefinedAttribute.label;
  }
  const aggregator = dataItem.split('_').pop();
  const aggregatedItem = aggregationInfoList?.find(
    ({ aggregMethods, inputDataItem }) =>
      aggregMethods[aggregator] && dataItem.includes(inputDataItem)
  );
  return aggregatedItem ? `${aggregatedItem.inputDataItem}_${aggregator}` : dataItem;
};

/**
 * Gets the available aggregation methods for a data item based on the dataItem type
 * @param {object} dataItem
 * @param {array<object>} dataItems
 * @param {string} cardType
 * @returns {array} array of available aggregation methods
 */
export const getAggregMethodsForDataItem = (
  dataItem,
  dataItems: Array<DataItemType>,
  cardType: CardTypes
): { id: string; text: string }[] => {
  /** list of which aggreg methods are valid for each type */
  let availableAggregMethods = [
    { id: 'none' },
    { id: 'mean', type: COLUMNTYPE_NUMBER }, // not for literals
    { id: 'min', type: COLUMNTYPE_NUMBER }, // not for literals
    { id: 'max', type: COLUMNTYPE_NUMBER }, // not for literals
    { id: 'last' },
    { id: 'count' },
    { id: 'sum', type: COLUMNTYPE_NUMBER }, // not for literals
    { id: 'first' },
    { id: 'std', type: COLUMNTYPE_NUMBER }, // not for literals
  ];

  const dataItemWithColumnType = dataItem.columnType
    ? dataItem
    : dataItems.find(item => item.name === dataItem.rawId && item.columnType);

  if (!dataItemWithColumnType) {
    return [];
  }

  // Dimensions should not allow any aggregation
  if (isDataItemDimension(dataItemWithColumnType)) {
    availableAggregMethods = [{ id: 'none' }];
  } // remove the none option for VALUE and IMAGE cards
  else if (cardType === CARD_TYPES.VALUE || cardType === CARD_TYPES.IMAGE) {
    availableAggregMethods = availableAggregMethods.filter(method => method.id !== 'none');
  }
  // If the data item is an alert, then only count can be done
  if (isDataItemAlert(dataItemWithColumnType)) {
    availableAggregMethods = availableAggregMethods.filter(
      method => method.id === 'count' || method.id === 'none'
    );
    // for boolean types, count or last
  } else if (dataItemWithColumnType?.columnType === COLUMNTYPE_BOOLEAN) {
    availableAggregMethods = availableAggregMethods.filter(
      // only show methods valid for our current column type
      method => method.id === 'count' || method.id === 'last'
    );
  }

  // no matter what only show methods valid for our current column type
  availableAggregMethods = availableAggregMethods.filter(
    method => !method.type || method.type === dataItemWithColumnType?.columnType
  );

  return availableAggregMethods.map(i => ({ id: i.id, text: capitalize(i.id) }));
};

/**
 * Determines what aggregator to default to, if any
 * @param {object} card
 * @returns {array}
 */
export const determineDefaultAggregator = (
  newCard: Card,
  isSummaryDashboard: boolean,
  dataItem: DataItemType
): string => {
  if (dataItem && isDataItemAlert(dataItem)) {
    return 'count';
  }
  return newCard.type === CARD_TYPES.TABLE && !newCard.dataSource?.groupBy
    ? 'none' // raw attributes are allowed for non-grouped table cards
    : isSummaryDashboard
    ? !dataItem || isDataItemNumeric(dataItem)
      ? 'mean'
      : 'last'
    : isTimeBasedCard(newCard)
    ? 'none'
    : 'last';
};

/**
 * Gets the aggregation method of a data item, if there is one
 * @param {object} dataItem, full data item object to investigate
 * @param {object} aggregationInfoList list of aggregation info from summary dashboard
 * @param {object} card used to determine the default aggregator if not set through other means
 * @param {boolean} isSummaryDashboard
 * @param {boolean} isDataSourceAttribute
 * @param {object} dataSourceAttribute
 * @returns {string} aggregation method (ex: last)
 */
export const getAggregMethodForDataItem = (
  dataItem,
  aggregationInfoList: Array<AggregationInfo>,
  card: Card,
  isSummaryDashboard: boolean,
  isDataSourceAttribute: boolean,
  dataSourceAttribute
): string => {
  // For dimensional dataItems we should never aggregate
  if (isDataItemDimension(dataItem)) {
    return 'none';
  }
  // If it's already set, use it
  if (isDataSourceAttribute && dataSourceAttribute.aggregator) {
    return dataSourceAttribute.aggregator;
  }
  // If the data item is in the aggregationInfoList then we should default the aggregator from there
  if (isSummaryDashboard) {
    const parentRawItemInfo = aggregationInfoList?.find(
      ({ aggregMethods }) => Object.values(aggregMethods)?.includes(dataItem.name)
    );
    if (parentRawItemInfo) {
      const aggregMethodEntries = Object.entries(parentRawItemInfo.aggregMethods);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return aggregMethodEntries.find(([method, itemName]) => dataItem.name === itemName)[0];
    }
  }

  // otherwise use the default aggregator
  return determineDefaultAggregator(card, isSummaryDashboard, dataItem);
};

/**
 * Takes our grain string and converts to what the PAL editor is looking for
 * @param {*} grain
 * @returns {string} the string that's compatible with their format
 */
const convertOurGrainToPALFormat = (grain: string): string => {
  switch (grain) {
    case TIME_GRAINS.HOUR:
      return 'hourly';
    case TIME_GRAINS.DAY:
      return 'daily';
    case TIME_GRAINS.WEEK:
      return 'weekly';
    case TIME_GRAINS.MONTH:
      return 'monthly';
    case TIME_GRAINS.YEAR:
      return 'yearly';
    case 'input':
    default:
      return undefined;
  }
};

/**
 * Formats data items with aggregation and dashboard data that is data item specific
 * @param {object} dataItem either the dataItem from the dataSource.attributes section or the dataItem from our metadata
 * @param {object} userDefinedAttribute
 * @param {array<object>} dataItems
 * @param {array<object>} aggregationInfoList list of aggregation info
 * @param {string} grain the grain of the dashboard
 * @param {object} card
 * @param {boolean} isSummaryDashboard the summary dashboard
 * @param {boolean} hasStreamingMetricEnabled used to enable the V2 aggregation methods
 * @returns {object} new dataItem object in the format that the editor expects
 */
export const formatDataItemWithMetaData = (
  dataItem: DataSourceAttribute | DataItemType | DataItemTypeWithDestination,
  userDefinedAttribute: any | null,
  dataItems: Array<DataItemType>,
  aggregationInfoList: Array<AggregationInfo>,
  grain: string,
  card: Card,
  isSummaryDashboard: boolean,
  hasStreamingMetricEnabled = false
): any => {
  // dataItem could be a dataSource attribute or a full dataItem. Need to make sure we have the full item
  // if the item is a datasource attribute, the aggregator is already available
  const isDataSourceAttribute = !dataItem.hasOwnProperty('name');
  const matchingDataItem = (
    isDataSourceAttribute
      ? dataItems.find(({ name }) => name === (dataItem as DataSourceAttribute).attribute)
      : dataItem
  ) as DataItemType;

  // if we have an explicit aggregationMethod set use it, otherwise get a default
  let aggregationMethod = userDefinedAttribute?.aggregationMethod;
  let hasStreamingMetricEnabledOverride = false;

  if (matchingDataItem) {
    if (isDataItemRawMetric(matchingDataItem)) {
      hasStreamingMetricEnabledOverride = hasStreamingMetricEnabled;
    } else if (isDataItemDerived(matchingDataItem)) {
      hasStreamingMetricEnabledOverride = isDataItemStreamingMetric(matchingDataItem);
    }

    if (isNil(aggregationMethod)) {
      if (hasStreamingMetricEnabledOverride) {
        // For dimensional dataItems we should never aggregate
        if (isDataItemDimension(dataItem)) {
          aggregationMethod = 'none';
        } else if (isDataSourceAttribute && (dataItem as DataSourceAttribute).aggregator) {
          // If it's already set, use it
          aggregationMethod = (dataItem as DataSourceAttribute).aggregator;
        } else {
          aggregationMethod = 'none';
        }
      } else {
        // if we can't find the matching data item then skip
        aggregationMethod = getAggregMethodForDataItem(
          matchingDataItem,
          aggregationInfoList,
          card,
          isSummaryDashboard,
          isDataSourceAttribute,
          dataItem
        );
      }
    }
  }

  const itemNameCount = countBy(dataItems, 'name');

  return matchingDataItem
    ? {
        ...userDefinedAttribute,
        // This is so that the editor can keep track of the true dataItem name as the dataSourceId is subject to change
        dataItemId: matchingDataItem?.name,
        // if the data source already has the attribute then the dataSourceId has been set
        dataSourceId: isDataSourceAttribute
          ? (dataItem as DataSourceAttribute).id
          : matchingDataItem?.name,
        label: formatDataItemLabel(
          matchingDataItem?.type === DATAITEM_TYPE_METRIC
            ? formatDataItemName(matchingDataItem, itemNameCount)
            : matchingDataItem?.name,
          aggregationInfoList,
          userDefinedAttribute
        ),
        ...((matchingDataItem as DataItemTypeWithDestination)?.destination
          ? { destination: (matchingDataItem as DataItemTypeWithDestination)?.destination }
          : {}),
        ...(matchingDataItem?.eventName ? { eventName: matchingDataItem.eventName } : {}),
        /** Do not use this type in conditon checking please use columnType and dataItemType */
        // pass type along from columnType (and also special handle dimensions)
        ...(isDataItemDimension(matchingDataItem)
          ? { type: DATAITEM_TYPE_DIMENSION }
          : { type: matchingDataItem.columnType }),
        dataItemType: matchingDataItem.type,
        columnType: matchingDataItem.columnType,
        // This is used for handleOnAddAggregation
        uuid: matchingDataItem.uuid,
        // This is used for handleHideAggregationsDropDown
        kpiFunctionDto: matchingDataItem.kpiFunctionDto,
        aggregationMethod,
        ...(hasStreamingMetricEnabledOverride
          ? {}
          : {
              aggregationMethods: getAggregMethodsForDataItem(
                matchingDataItem,
                dataItems,
                card.type
              ),
            }),
        hasStreamingMetricEnabled: hasStreamingMetricEnabledOverride,
        // if the card has a timegrain set already then use it, otherwise default to the summary Dashboard
        grain:
          // if the field has a grain set manually then use it
          userDefinedAttribute?.grain ||
          // other wise default grain should be empty or inherit from the card or summary dashboard
          (aggregationMethod === 'none'
            ? undefined
            : card.dataSource?.timeGrain || grain // if we have a grain already on the card or summary dashboard, use it
            ? convertOurGrainToPALFormat(card?.dataSource?.timeGrain) || grain?.toLowerCase()
            : convertOurGrainToPALFormat(TIME_GRAINS.HOUR)), // otherwise default aggregationMethods to hourly
      }
    : userDefinedAttribute;
};

/**
 * Adds the necessary metadata for editing a card
 * @param {Object} card
 * @param {Array<Object>} dataItems
 * @param {Array<Object>} aggregationInfoList
 * @param {string} grain
 * @param {Boolean} isSummaryDashboard
 */
export const formatCardWithMetaData = (
  card: Card,
  dataItems: Array<DataItemType>,
  aggregationInfoList: Array<AggregationInfo>,
  grain: string,
  isSummaryDashboard: boolean,
  hasStreamingMetricEnabled: boolean
): Card => {
  switch (card.type) {
    case CARD_TYPES.TIMESERIES:
    case CARD_TYPES.BAR:
      // Performance improvement, only regenerate the card series metadata if it needs updating
      return card?.content?.series?.some(line => !line.aggregationMethods)
        ? {
            ...card,
            content: {
              ...card.content,
              series: card.content?.series?.map(line => {
                const matchingAttribute = card.dataSource?.attributes?.find(
                  ({ id }) => id === line.dataSourceId
                );
                return matchingAttribute
                  ? formatDataItemWithMetaData(
                      matchingAttribute,
                      line,
                      dataItems,
                      aggregationInfoList,
                      grain,
                      card,
                      isSummaryDashboard,
                      hasStreamingMetricEnabled
                    )
                  : line;
              }),
            },
          }
        : card;
    case CARD_TYPES.VALUE:
      return {
        ...card,
        content: {
          ...card.content,
          attributes: card.content?.attributes?.map(attribute => {
            const matchingAttribute = card.dataSource?.attributes?.find(
              ({ id }) => id === attribute.dataSourceId
            );
            return matchingAttribute
              ? formatDataItemWithMetaData(
                  matchingAttribute,
                  attribute,
                  dataItems,
                  aggregationInfoList,
                  grain,
                  card,
                  isSummaryDashboard,
                  hasStreamingMetricEnabled
                )
              : attribute;
          }),
        },
      };
    case CARD_TYPES.TABLE:
      return {
        ...card,
        content: {
          ...card.content,
          columns: card.content?.columns?.map(column => {
            // skip timestamp column because we don't need to format them
            if (
              column.type === COLUMNTYPE_TIMESTAMP ||
              column.columnType === COLUMNTYPE_TIMESTAMP
            ) {
              return column;
            }
            const matchingAttribute = card.dataSource?.attributes?.find(
              ({ id }) => id === column.dataSourceId
            );

            let dimensionalDataItem;
            // dimensions will not have matchingAttribute => add it from dataItems
            if (isNil(matchingAttribute)) {
              const matchingDataItem = dataItems.find(({ name }) => name === column.dataSourceId);
              if (matchingDataItem?.type === DATAITEM_TYPE_DIMENSION) {
                dimensionalDataItem = matchingDataItem;
              }
            }

            return matchingAttribute || dimensionalDataItem
              ? formatDataItemWithMetaData(
                  matchingAttribute || dimensionalDataItem,
                  column,
                  dataItems,
                  aggregationInfoList,
                  grain,
                  card,
                  isSummaryDashboard,
                  hasStreamingMetricEnabled
                )
              : column;
          }),
        },
      };
    case CARD_TYPES.IMAGE:
      return {
        ...card,
        values: {
          ...card.values,
          hotspots: card?.values?.hotspots?.map(hotspot => ({
            ...hotspot,
            content: {
              ...hotspot.content,
              attributes: hotspot.content?.attributes?.map(attribute => {
                const matchingAttribute = card.dataSource?.attributes?.find(
                  ({ id }) => id === attribute.dataSourceId
                );

                let dimensionalDataItem;
                // dimensions will not have matchingAttribute => add it from dataItems
                if (isNil(matchingAttribute)) {
                  const matchingDataItem = dataItems.find(
                    ({ name }) => name === attribute.dataSourceId
                  );
                  if (matchingDataItem?.type === DATAITEM_TYPE_DIMENSION) {
                    dimensionalDataItem = matchingDataItem;
                  }
                }

                return matchingAttribute || dimensionalDataItem
                  ? formatDataItemWithMetaData(
                      matchingAttribute || dimensionalDataItem,
                      attribute,
                      dataItems,
                      aggregationInfoList,
                      grain,
                      card,
                      isSummaryDashboard,
                      hasStreamingMetricEnabled
                    )
                  : attribute;
              }),
            },
          })),
        },
      };
    default:
      return card;
  }
};

/**
 * Goes through the contents sections of cards that are dynamically created by the dashboard and
 * includes meta data based on the parent data item
 * (ex: cards containing new aggregation in the create new summary dashboard flow)
 * @param {object} templateProp dashboard template
 * @param {object} dataItems full list of data items available to the dashboard
 * @param {object} aggregationInfoList list of aggregation info
 * @param {string} grain the grain of the dashboard
 * @returns {object} template object with updated contents sections
 */
export const formatExistingTemplateWithMetaData = (
  templateProp: DashboardTemplate,
  dataItems: Array<DataItemType>,
  aggregationInfoList: Array<AggregationInfo>,
  grain: string,
  isSummaryDashboard: boolean,
  hasStreamingMetricEnabled: boolean
): DashboardTemplate => {
  const template = { ...templateProp };
  const cardsWithMetaData = template.cards?.map(card =>
    formatCardWithMetaData(
      card,
      dataItems,
      aggregationInfoList,
      grain,
      isSummaryDashboard,
      hasStreamingMetricEnabled
    )
  );

  return {
    ...template,
    cards: cardsWithMetaData,
  };
};

/**
 * Filters the dataItems for the dashboard editor so that the user doesn't try to put a dataItem
 * on a card that can not understand it.
 * Returns additional metadata for each data Item to allow the Dashboard Editor
 * to understand the options for aggregation that can be used.
 * @param {object} card
 * @param {string} timeGrain
 * @param {array} dataItems
 * @returns {array} array of valid dataItems based on card type
 */
export const getValidDataItems = (
  card: Card,
  timeGrain: string,
  dataItems: Array<DataItemType>,
  aggregationInfoList: Array<AggregationInfo> = [],
  summary: SummaryDto,
  entityTypeName: string,
  hasStreamingMetricEnabled = false
): DataItemType[] => {
  const isSummaryDashboard = !!summary; // isSummaryDashboard
  const { type } = card;
  const grain = summary?.granularityTimeFrequency;

  const newAggregatedItems = [];
  aggregationInfoList.forEach(
    ({ aggregMethods = {} }) =>
      // find any aggregated items that don't exist as data items yet so that they can be selected
      Object.values(aggregMethods)
        ?.filter(
          aggregatedItemName => !dataItems.find(dataItem => dataItem.name === aggregatedItemName)
        )
        .forEach(aggregatedItemName => {
          newAggregatedItems.push({
            name: aggregatedItemName,
            type: DATAITEM_TYPE_CALCULATED_METRIC,
            columnType: COLUMNTYPE_NUMBER,
          });
        })
  );

  let validDataItems = dataItems;
  // currently our alerts don't support custom data items so return empty array
  if (
    type === CUSTOM_CARD_TYPES.ALERT ||
    type === CUSTOM_CARD_TYPES.THIRD_PARTY ||
    type === CARD_TYPES.CUSTOM
  ) {
    validDataItems = [];
  } else {
    // if the card datasource type does not equal our entitytype do not append the aggregated ones from the summary dashboard, otherwise do
    validDataItems =
      card?.dataSource?.type && card?.dataSource?.type !== entityTypeName
        ? dataItems
        : dataItems.concat(newAggregatedItems);
  }

  switch (type) {
    case CARD_TYPES.TIMESERIES:
    case CARD_TYPES.BAR:
      // dimensions are not graphable, they are only valid for group bys
      // only allow numeric, non-dimensional items, booleans can be counted
      validDataItems = validDataItems
        .filter(dataItem => !isDataItemDimension(dataItem))
        .filter(
          dataItem => isDataItemNumeric(dataItem) || dataItem?.columnType === COLUMNTYPE_BOOLEAN
        );
      break;
    case CUSTOM_CARD_TYPES.ALERT:
    case CUSTOM_CARD_TYPES.THIRD_PARTY:
    case CARD_TYPES.CUSTOM:
      // currently our alerts don't support custom data items so return empty array
      validDataItems = [];
      break;
    case CARD_TYPES.IMAGE:
      // allow any dataItem type on ImageCard
      break;
    case CARD_TYPES.VALUE:
    case CARD_TYPES.TABLE:
      // allow any dataItem type on ValueCard or TableCard along with deviceid
      validDataItems = validDataItems.concat(
        entityTypeName
          ? // allow tables and value cards to have deviceid as a data item
            [
              {
                name: 'deviceid',
                type: DATAITEM_TYPE_DIMENSION,
                columnType: COLUMNTYPE_LITERAL,
                destination: 'groupBy',
              } as DataItemTypeWithDestination,
            ]
          : []
      );
      break;
    default:
      break;
  }

  validDataItems = validDataItems.map(dataItem =>
    formatDataItemWithMetaData(
      dataItem,
      null,
      dataItems,
      aggregationInfoList,
      grain,
      card,
      isSummaryDashboard,
      hasStreamingMetricEnabled
    )
  );

  return sortBy(validDataItems, 'label');
};

/**
 * Determine if any aggregators have been changed and update the summaryConfig accordingly, examines the content section of the cards
 * to make this determination.  TODO: should update this to handle the add and the delete case for new attributes
 * @param {*} summaryConfig
 * @param {*} newAttributes list of newly added attributes with dataItemId, dataSourceId and aggregationMethod set
 * @param {*} removedAttributes list of removed attributes with dataItemId, dataSourceId and aggregationMethod
 * @param {*} updatedAttributes list of newly updated attributes with dataItemId, dataSourceId and aggregationMethod, updated attributes could have aggregationMethod or dataSourceId updated
 * @returns {*} updatedAggregationList the new aggregationInfoList the summaryConfig should be using, each record looks like this
 *              {inputDataItem: attribute.dataItemId, aggregMethods: { [attribute.aggregationMethod]: attribute.dataSourceId }, catalogFunction: 'AggregateItems', }
 */
export const determineAggregationInfoList = (
  summaryConfig: Readonly<SummaryDto>,
  newAttributes: Readonly<CardAttributes[]> = [],
  removedAttributes: Readonly<CardAttributes[]> = [],
  updatedAttributes: Readonly<CardAttributes[]> = []
): AggregationInfo[] => {
  if (
    summaryConfig &&
    (!isEmpty(removedAttributes) || !isEmpty(newAttributes) || !isEmpty(updatedAttributes))
  ) {
    // if the attribute is a dimension, we need to bail out

    // for each of the newAttributes, we need to see if the data item already exists in the aggregationInfoList

    // first build a list of data items so I know whether to update the existing one or add a new one
    const existingAggregationDataItems = summaryConfig.aggregationInfoList.map(
      aggregationInfo => aggregationInfo.inputDataItem
    );

    // go through the newAttributes for that card, check to see if it's in aggregationInfoList already
    // if the inputDataItem already exists add a new aggregMethod to it if it doesn't exist
    // otherwise add both the dataItem and the aggregMethod to the aggregationInfoList

    const aggregationInfoListToAdd = [];
    const aggregationInfoListToUpdate = [];
    const aggregationInfoListToRemove = [];

    // first figure out which attributes we should really add, some might already
    // exist in the summaryConfig due to other cards
    newAttributes
      // attributes without aggregation methods should not be added to the aggregationInfoList
      .filter(attribute => attribute.aggregationMethod && attribute.aggregationMethod !== 'none')
      .forEach(attribute => {
        // only add if its not a dimension
        if (!attribute.isDimension) {
          if (existingAggregationDataItems.includes(attribute.dataItemId)) {
            // Because the card might be adding an new attribute to itself that's being used in another card, we do want to check if it's already there
            const indexOfExistingDataItem = summaryConfig.aggregationInfoList.findIndex(
              aggregationInfo =>
                aggregationInfo.inputDataItem === attribute.dataItemId &&
                Object.keys(aggregationInfo.aggregMethods).includes(attribute.aggregationMethod)
            );
            // the data item does exist, but the specific aggregator doesn't exist so I need to add to the update list
            // OR the data item exists but the data Source Id has changed
            if (
              indexOfExistingDataItem === -1 ||
              summaryConfig.aggregationInfoList.find(
                aggregationInfo =>
                  aggregationInfo.inputDataItem === attribute.dataItemId &&
                  Object.keys(aggregationInfo.aggregMethods).includes(
                    attribute.aggregationMethod
                  ) &&
                  aggregationInfo.aggregMethods[attribute.aggregationMethod] !==
                    attribute.dataSourceId
              )
            ) {
              aggregationInfoListToUpdate.push({
                inputDataItem: attribute.dataItemId,
                aggregMethods: { [attribute.aggregationMethod]: attribute.dataSourceId },
                catalogFunction: 'AggregateItems',
              });
            }

            // Noop because the attribute and aggregator already exist in the Summary Dashboard
          } else {
            // if the data item already exists we need to add the new aggregMethod
            const existingDataItem = aggregationInfoListToAdd.find(
              aggregationInfo => aggregationInfo.inputDataItem === attribute.dataItemId
            );
            if (existingDataItem) {
              aggregationInfoListToAdd[
                aggregationInfoListToAdd.findIndex(
                  aggregationInfo => aggregationInfo.inputDataItem === attribute.dataItemId
                )
              ] = {
                ...existingDataItem,
                aggregMethods: {
                  ...existingDataItem.aggregMethods,
                  [attribute.aggregationMethod]: attribute.dataSourceId,
                },
              };
            } else {
              // the data item doesn't exist at all, so we need to add it
              aggregationInfoListToAdd.push({
                inputDataItem: attribute.dataItemId,
                aggregMethods: { [attribute.aggregationMethod]: attribute.dataSourceId },
                catalogFunction: 'AggregateItems',
              });
            }
          }
        }
      });

    // now figure out which attributes we should really remove
    removedAttributes
      .filter(attribute => attribute.aggregationMethod && attribute.aggregationMethod !== 'none')
      .forEach(attribute => {
        if (existingAggregationDataItems.includes(attribute.dataItemId)) {
          // Do we already have an existing item in the remove list that we need to update
          const existingDataItemInRemoveList = aggregationInfoListToRemove.find(
            aggregationInfo => aggregationInfo.inputDataItem === attribute.dataItemId
          );

          if (existingDataItemInRemoveList) {
            aggregationInfoListToRemove[
              aggregationInfoListToRemove.findIndex(
                aggregationInfo => aggregationInfo.inputDataItem === attribute.dataItemId
              )
            ] = {
              // add the new aggregMethod to the existing remove list item
              ...existingDataItemInRemoveList,
              aggregMethods: {
                ...existingDataItemInRemoveList.aggregMethods,
                [attribute.aggregationMethod]: attribute.dataSourceId,
              },
            };
          } else {
            // otherwise add a new remove list item
            aggregationInfoListToRemove.push({
              inputDataItem: attribute.dataItemId,
              aggregMethods: { [attribute.aggregationMethod]: attribute.dataSourceId },
              catalogFunction: 'AggregateItems',
            });
          }
        }
        // if it never existed do nothing
      });

    // now figure out which attributes we should updaate
    updatedAttributes
      .filter(attribute => attribute.aggregationMethod && attribute.aggregationMethod !== 'none')
      .forEach(attribute => {
        // for each updated data item, need to add the aggregation Method to add to it
        aggregationInfoListToUpdate.push({
          inputDataItem: attribute.dataItemId,
          aggregMethods: { [attribute.aggregationMethod]: attribute.dataSourceId },
          catalogFunction: 'AggregateItems',
        });
      });
    // Need to remove the old instances of the aggregMethods, and add the new ones
    const aggregationInfoList = [];

    // First handle the remove and update case for existing aggregators
    summaryConfig.aggregationInfoList.forEach(aggregationInfo => {
      // First check the remove case
      const aggregatorToRemove = aggregationInfoListToRemove.find(
        removeAggregationInfo =>
          removeAggregationInfo.inputDataItem === aggregationInfo.inputDataItem &&
          // ensure the existing aggregMethod has some keys that match this removeAggregationInfo.aggregMethods object
          Object.keys(aggregationInfo.aggregMethods).find(
            aggregMethod => removeAggregationInfo.aggregMethods[aggregMethod]
          )
      );
      // if I actually have this aggregator in my infoList that matches
      // if we are removing all the attributes, just skip the addition
      if (
        aggregatorToRemove &&
        !isEqual(aggregationInfo.aggregMethods, aggregatorToRemove.aggregMethods)
      ) {
        aggregationInfoList.push({
          ...aggregationInfo,
          // omit object property from aggregMethods that matches the aggregatorToRemove
          aggregMethods: omit(
            aggregationInfo.aggregMethods,
            Object.keys(aggregatorToRemove.aggregMethods)
          ),
        });
      }

      // Now check the update case, does this exact aggregator with this dataSourceId already exist
      const existingAggregator = aggregationInfoListToUpdate.find(
        updateAggregationInfo =>
          updateAggregationInfo.inputDataItem === aggregationInfo.inputDataItem &&
          aggregationInfo.aggregMethods[Object.keys(updateAggregationInfo.aggregMethods)[0]] &&
          aggregationInfo.aggregMethods[Object.keys(updateAggregationInfo.aggregMethods)[0]] ===
            Object.values(updateAggregationInfo.aggregMethods)[0]
      );

      const aggregatorToUpdate = aggregationInfoListToUpdate.find(
        updateAggregationInfo =>
          updateAggregationInfo.inputDataItem === aggregationInfo.inputDataItem
      );

      // if we don't already have this aggreg method on the current data item then add it
      if (!existingAggregator && aggregatorToUpdate) {
        aggregationInfoList.push({
          ...aggregationInfo,
          // add the object property to aggregMethods that we need to update
          aggregMethods: { ...aggregationInfo.aggregMethods, ...aggregatorToUpdate.aggregMethods },
        });
      }

      // if we neither need to update or remove this aggregator, then just add it to our list
      if (!aggregatorToUpdate && !aggregatorToRemove) {
        aggregationInfoList.push(aggregationInfo);
      }
    });

    // need to check if there was a new data item to update that didn't previously exist in the aggregationInfoList
    // This is a special case where the data item already existed, but it was not aggregated
    aggregationInfoListToUpdate.forEach(updateAggregationInfo => {
      if (
        !aggregationInfoList.find(
          aggregationInfo => updateAggregationInfo.inputDataItem === aggregationInfo.inputDataItem
        )
      ) {
        aggregationInfoList.push(updateAggregationInfo);
      }
    });

    // Now check the add case for each of the aggregation info lists to add
    aggregationInfoListToAdd.forEach(addAggregationInfo => {
      // // If the existing aggregator already exists in the aggregationInfoList then do nothing
      const existingAggregator = aggregationInfoList.find(
        aggregationInfo =>
          addAggregationInfo.inputDataItem === aggregationInfo.inputDataItem &&
          isEqual(aggregationInfo.aggregMethods, addAggregationInfo.aggregMethods)
      );

      if (!existingAggregator) {
        aggregationInfoList.push({
          ...addAggregationInfo,
        });
      }
      // }
      // if the aggregator already exists in the summary dashboard then don't add it
    });

    return aggregationInfoList;
  }
  // No detected changes, just return the normal list
  return summaryConfig?.aggregationInfoList;
};

// need the attribute map to search for new attributes, keyed by attribute id
const buildAttributeMap = (dataSourceAttributes, timeGrain): AttributeMap =>
  dataSourceAttributes?.reduce(
    (acc: AttributeMap, attribute: DataSourceAttribute): AttributeMap => {
      // if the attribute doesn't exist yet then add a new array
      if (!acc[attribute.id]) {
        acc[attribute.id] = {
          attribute: attribute.attribute,
          aggregators: [attribute.aggregator || 'none'],
          timeGrain,
          ...(attribute.eventName ? { eventName: attribute.eventName } : {}),
        };
      } else {
        acc[attribute.id].aggregators.push(attribute.aggregator || 'none');
      }

      return acc;
    },
    {}
  );

/**
 * This function compares the oldCard and newCard for all card types to determine the changes that have been made
 * to the attributes sections of the card.  It will examine existing attributes to ensure that the aggregators have not been updated.
 * @param oldCard the previous card config, each attribute definition now stores dataItemId, dataSourceId and aggregMethod for the selected aggregation
 * @param newCard the card with updates, each attribute points to dataItemId, dataSourceId and aggregMethod
 * @param dataItems full list of metadata on the data item
 *
 * @return [addedAttributes, removedAttributes, updatedAttributes] addedAttributes are any newly added dataItems,
 * removedAttributes are newly removed dataItems ,
 * updated Attributes are dataItems that formerly existed but are now getting new aggregators or new dataSourceIds, it will return multiple rows
 * for the same dataItem if you have multiple columns that point to the same underlying dataItem, it will list all the attributes whose data item has been updated
 */
export const determineAttributeChanges = (
  oldCard: Readonly<Card>,
  newCard: Readonly<Card>,
  dataItems: Readonly<DataItemType[]>
): [CardAttributes[], CardAttributes[], CardAttributes[]] => {
  const addedAttributes = [];
  const removedAttributes = [];
  const updatedAttributes = [];

  // new card attributes will be an object keyed by attribute name with each value set to
  const newCardAttributes = {};
  let oldCardAttributes = {};
  // for oldCard attributes, we can simple look at the dataSource, but want to map them to an object for easy comparison
  oldCardAttributes = buildAttributeMap(
    oldCard?.dataSource?.attributes,
    oldCard?.dataSource?.timeGrain
  );

  // for the new card attributes, we need to look at each dataItem in the actual content section versus
  // the ones in the actual oldCard attributes (which have already been mapped) inside the dataSource
  // TODO: do for other card types than table
  let newCardColumns;
  let oldCardColumns;
  switch (newCard.type) {
    case CARD_TYPES.TABLE:
      newCardColumns = newCard?.content?.columns || [];
      oldCardColumns = oldCard?.content?.columns || [];
      break;
    case CARD_TYPES.VALUE:
      newCardColumns = newCard?.content?.attributes || [];
      oldCardColumns = oldCard?.content?.attributes || [];
      break;
    case CARD_TYPES.TIMESERIES:
    case CARD_TYPES.BAR:
      newCardColumns = newCard?.content?.series || [];
      oldCardColumns = oldCard?.content?.series || [];
      break;
    case CARD_TYPES.IMAGE: {
      const newCardHotspots = newCard?.values?.hotspots || newCard?.content?.hotspots;
      const oldCardHotspots = oldCard?.values?.hotspots || oldCard?.content?.hotspots;
      newCardColumns = flatten(
        newCardHotspots?.map(newHotspot => newHotspot?.content?.attributes || [])
      );
      oldCardColumns = flatten(
        oldCardHotspots?.map(newHotspot => newHotspot?.content?.attributes || [])
      );
      const dynamicHotspots = newCardHotspots?.filter(
        newHotspot => typeof newHotspot.x === 'string' || typeof newHotspot.y === 'string'
      );

      // need to also add the dynamic hotspot fields since they might not be in the hotspot attributes
      dynamicHotspots?.forEach(hotspot => {
        // check x
        newCardColumns.push({
          dataSourceId: hotspot.x,
          dataItemId: hotspot.x,
          aggregationMethod: 'last',
        });
        // check y
        newCardColumns.push({
          dataSourceId: hotspot.y,
          dataItemId: hotspot.y,
          aggregationMethod: 'last',
        });
      });
      break;
    }
    default:
      newCardColumns = [];
      break;
  }

  /**
   * if no dataItemId is set, we came from JSON Editor and we should leave this card alone!
   */
  if (
    newCardColumns
      // Filter out hardcoded timestamp dataItem which was added in handleDataSeriesChange() function
      .filter(column =>
        newCard.type === CARD_TYPES.TABLE
          ? !(column.dataSourceId === 'timestamp' && column?.type === COLUMNTYPE_TIMESTAMP)
          : true
      )
      // Filter out hardcoded deviceid dataItem which was added in getValidDataItems() function
      .filter(column =>
        newCard.type === CARD_TYPES.TABLE
          ? !(column.dataSourceId === 'deviceid' && column.destination === 'groupBy')
          : true
      )
      .find(column => !column.dataItemId)
  ) {
    return [[], [], []];
  }

  // figure out which columns to add to the dataSource section
  newCardColumns.forEach(
    ({
      dataSourceId,
      dataItemId,
      aggregationMethod,
      destination,
      hasStreamingMetricEnabled,
      eventName,
    }) => {
      // only check this if it's not equal to timestamp since we don't put that in datasource
      // if we don't have the dataSource or dataSourceId at all, then add it
      if (dataItemId?.toLowerCase() !== 'timestamp' && !oldCardAttributes?.[dataSourceId]) {
        newCardAttributes[dataSourceId] = {
          attribute: dataItemId,
          destination,
          hasStreamingMetricEnabled,
          aggregators: [aggregationMethod || 'none'],
          ...(eventName ? { eventName } : {}),
        };
      }
    }
  );

  // map over the newcard attributes to set them to the added Attributes CardAttribute format
  if (!isEqual(newCardAttributes, oldCardAttributes)) {
    Object.keys(newCardAttributes)?.forEach(newDataSourceId => {
      const { attribute, aggregators, destination, eventName } = newCardAttributes[newDataSourceId];
      // Add each aggregated data item to the CardAttributes
      aggregators?.forEach(aggregator => {
        if (aggregator) {
          addedAttributes.push({
            dataSourceId: newDataSourceId,
            dataItemId: attribute,
            isDimension: Boolean(
              dataItems?.find(
                dataItem => dataItem.name === attribute && isDataItemDimension(dataItem)
              )
            ),
            aggregationMethod: aggregator,
            destination,
            ...(eventName ? { eventName } : {}), // if there is a eventName then save it
          });
        }
      });
    });

    // figure out which columns to remove from the card dataSource section
    if (oldCardAttributes) {
      Object.keys(oldCardAttributes).forEach(oldCardDataSourceId => {
        const matchingColumn = newCardColumns.find(
          column => column.dataSourceId === oldCardDataSourceId
        );

        const matchingExpandedRow = newCard?.content?.expandedRows?.find(
          row => row.dataSourceId === oldCardDataSourceId
        );

        // if you add new card attributes they typically get a new datasourceid
        // was the data source id removed, i.e. we can't find a column that matches
        const { attribute, aggregators, timeGrain } = oldCardAttributes[oldCardDataSourceId];
        if (!matchingColumn) {
          if (!matchingExpandedRow) {
            aggregators?.forEach(aggregator => {
              removedAttributes.push({
                dataSourceId: oldCardDataSourceId,
                isDimension: Boolean(
                  dataItems?.find(
                    dataItem => dataItem.name === attribute && isDataItemDimension(dataItem)
                  )
                ),
                aggregationMethod: aggregator,
                dataItemId: attribute,
              });
            });
          }
          // }
        } else {
          const { aggregationMethod, grain, eventName, dataFilter } = matchingColumn;

          // Save oldCardColumn, to check for dataFilter changes on matchingColumn
          const oldCardColumn = oldCardColumns?.find(
            column => column.dataSourceId === oldCardDataSourceId
          );

          // Generate what an updated column payload would look like
          const possiblyUpdatedColumn = {
            dataSourceId: oldCardDataSourceId,
            isDimension: Boolean(
              dataItems?.find(
                dataItem => dataItem.name === attribute && isDataItemDimension(dataItem)
              )
            ),
            ...(aggregationMethod ? { aggregationMethod } : {}),
            dataItemId: attribute,
            grain,
            ...(eventName ? { eventName } : {}), // if there is a eventName then save it
            dataFilter,
          };
          // if we find an existing columns aggregator has changed, we need to update that
          if (
            matchingColumn.grain !== convertOurGrainToPALFormat(timeGrain) ||
            (!isEmpty(aggregators) && aggregationMethod && aggregationMethod !== aggregators[0]) ||
            Object.keys(oldCardColumn?.dataFilter || {})[0] !== Object.keys(dataFilter || {})[0] // if dataFilter has changed
          ) {
            updatedAttributes.push(possiblyUpdatedColumn);
          }
        }
      });
    }
  }

  return [addedAttributes, removedAttributes, updatedAttributes];
};

/**
 *
 * @param {*} updatedCard  the newest card
 * @param {*} newAttributes totally new dataItems
 * @param {*} removedAttributes removed data items
 * @param {*} updatedAttributes dataItems that already exist that should be updated
 *
 * @returns {array} new array of datasource attributes for the new card
 */
export const determineDataSourceAttributes = (
  updatedCard: Card,
  newAttributes: CardAttributes[] = [],
  removedAttributes: CardAttributes[] = [],
  updatedAttributes: CardAttributes[] = []
): DataSourceAttribute[] =>
  uniqBy(
    sortBy(
      (updatedCard?.dataSource?.attributes || [])
        .filter(
          // remove any removedAttribute from the existing dataSource in the updatedCard
          attribute =>
            !removedAttributes.find(removedColumn => attribute.id === removedColumn.dataSourceId) &&
            // handle the updated Attributes later
            !updatedAttributes.find(updatedColumn => attribute.id === updatedColumn.dataSourceId)
        )
        .concat(
          // add any Attribute from the existing dataSource in the updatedCard
          newAttributes.map(
            newColumn =>
              ({
                id: newColumn.dataSourceId,
                attribute: newColumn.dataItemId,
                ...(newColumn.aggregationMethod && newColumn.aggregationMethod !== 'none'
                  ? { aggregator: newColumn.aggregationMethod }
                  : {}),
                ...(newColumn.eventName ? { eventName: newColumn.eventName } : {}),
              }) as DataSourceAttribute
          )
        )
        .concat(
          updatedAttributes.map(
            columnWithUpdates =>
              ({
                ...(columnWithUpdates.aggregationMethod &&
                columnWithUpdates.aggregationMethod !== 'none'
                  ? { aggregator: columnWithUpdates.aggregationMethod }
                  : {}),
                id: columnWithUpdates.dataSourceId,
                attribute: columnWithUpdates.dataItemId,
                ...(columnWithUpdates.eventName ? { eventName: columnWithUpdates.eventName } : {}),
              }) as DataSourceAttribute
          )
        ),
      'attribute'
    ),
    'id'
  );

/**
 * Searches the aggregationInfoList for a dataItemId and aggregation Method and returns the new aggregated data item name if it exists
 * @param {string} dataItemId the id of the data Item to look for
 * @param {string} aggregationMethod the name of the aggregation Method to search for
 * @param {Array<Object>} aggregationInfoList Each row in the aggregationInfoList looks like this {inputDataItem: attribute.dataItemId,
 *           aggregMethods: { [attribute.aggregationMethod]: attribute.dataSourceId },
 *        catalogFunction: 'AggregateItems',
 * @returns {string} updatedDataItemId returns the newly created aggregated data item ID if it exists in the list, otherwise returns the raw data item id
 */
export const findNewAggregatedDataItemId = (
  dataItemId: string,
  aggregationMethod: string,
  aggregationInfoList: Array<AggregationInfo>
): string => {
  const matchingAggregatedDataItem = aggregationInfoList?.find(
    aggregationInfo =>
      aggregationInfo.inputDataItem === dataItemId &&
      aggregationInfo.aggregMethods[aggregationMethod]
  );
  return matchingAggregatedDataItem
    ? matchingAggregatedDataItem.aggregMethods[aggregationMethod]
    : dataItemId;
};
/**
 * The PAL data item editor doesn't know anything about summary dashboards, so it will give us a raw dataItemId.
 * However, there might be a preaggregated attribute available at the Summary Dashboard level that will improve our performance that we should use.
 * In this case we need to update the attributes section to point to the aggregated version of the item, not the raw one in this case
 * @param {*} card the current card configuration where each attribute has dataItemId, dataSourceId, aggregationMethod set on it
 * @param aggregationInfoList for summary dashboards, the data item might be created there, so we need to use the newly created one rather than the raw one
 * Each row in the aggregationInfoList looks like this {inputDataItem: attribute.dataItemId,
 *           aggregMethods: { [attribute.aggregationMethod]: attribute.dataSourceId },
 *        catalogFunction: 'AggregateItems',
 * @returns {object} updated Card
 */
export const replaceRawAttributeWithSummaryDashboardAttribute = (
  card: Card,
  aggregationInfoList: Array<AggregationInfo>
): Card => {
  switch (card.type) {
    case CARD_TYPES.TABLE:
      return {
        ...card,
        content: {
          ...card.content,
          columns: card?.content?.columns.map(column => ({
            ...column,
            dataItemId: findNewAggregatedDataItemId(
              column.dataItemId,
              column.aggregationMethod,
              aggregationInfoList
            ),
          })),
        },
      };
    case CARD_TYPES.VALUE:
      return {
        ...card,
        content: {
          ...card.content,
          attributes: card?.content?.attributes.map(column => ({
            ...column,
            dataItemId: findNewAggregatedDataItemId(
              column.dataItemId,
              column.aggregationMethod,
              aggregationInfoList
            ),
          })),
        },
      };
    case CARD_TYPES.TIMESERIES:
    case CARD_TYPES.BAR:
      return {
        ...card,
        content: {
          ...card?.content,
          series: card?.content?.series.map(column => ({
            ...column,
            dataItemId: findNewAggregatedDataItemId(
              column.dataItemId,
              column.aggregationMethod,
              aggregationInfoList
            ),
          })),
        },
      };
    case CARD_TYPES.IMAGE:
      return {
        ...card,
        values: {
          ...card.values,
          hotspots: card?.values?.hotspots?.map(hotspot => ({
            ...hotspot,
            content: {
              ...hotspot.content,
              attributes: hotspot.content.attributes?.map(attribute => ({
                ...attribute,
                dataItemId: findNewAggregatedDataItemId(
                  attribute.dataItemId,
                  attribute.aggregationMethod,
                  aggregationInfoList
                ),
              })),
            },
          })),
        },
      };
    default:
      return card;
  }
};

/**
 * Checks all the attributes of a card to determine what the grain should be and translates from the PAL
 * JSON Editor to our internal grain string
 * @param {*} addedAttributes
 * @param {*} updatedAttributes
 */
export const determineDataSourceTimeGrain = (
  addedAttributes: CardAttributes[] = [],
  updatedAttributes: CardAttributes[] = []
): string => {
  const cardGrain =
    addedAttributes.find(newAttribute => newAttribute.grain)?.grain ||
    updatedAttributes.find(updatedAttribute => updatedAttribute.grain)?.grain;
  switch (cardGrain?.toLowerCase()) {
    case 'hourly':
      return TIME_GRAINS.HOUR;
    case 'daily':
      return TIME_GRAINS.DAY;
    case 'weekly':
      return TIME_GRAINS.WEEK;
    case 'monthly':
      return TIME_GRAINS.MONTH;
    case 'yearly':
      return TIME_GRAINS.YEAR;
    default:
  }
  // if an aggregator is specified, but no time grain, default to day
  return addedAttributes.find(newAttribute => newAttribute.aggregationMethod) ||
    updatedAttributes.find(updatedAttribute => updatedAttribute.aggregationMethod)
    ? TIME_GRAINS.DAY
    : null;
};

/**
 * Builds the groupBy list based on any dataFilters that might be specified for the attributes
 * @param {*} addedAttributes any new attributes
 * @param {*} updatedAttributes any attributes with updates
 */
export const determineDataFilterGroupBy = (
  addedAttributes: CardAttributes[],
  updatedAttributes: CardAttributes[]
): Array<string> => {
  const dataFilterGroupBy = [];
  // Determine whether a groupBy is needed for the dataFilters
  if (addedAttributes.some(attribute => !isEmpty(attribute.dataFilter))) {
    dataFilterGroupBy.push(
      ...flatten(
        addedAttributes
          .filter(attribute => attribute.dataFilter)
          .map(attribute => Object.keys(attribute.dataFilter))
      )
    );
  }
  if (updatedAttributes.some(attribute => !isEmpty(attribute.dataFilter))) {
    dataFilterGroupBy.push(
      ...flatten(
        updatedAttributes
          .filter(attribute => attribute.dataFilter)
          .map(attribute => Object.keys(attribute.dataFilter))
      )
    );
  }
  return dataFilterGroupBy;
};

/**
 * This helper method compares the new card content section to the old card content section to determine
 * how to update the dataSource section attributes.  TODO: It should also call the setSummaryConfig to add any
 * new attributes to a SummaryDashboard, but I've had to disable this
 *
 * @param {object} updatedCard
 * @param {object} oldCard
 * @param {boolean} isSummaryDashboard, is this a summaryDashboard
 * @param {boolean} isHierarchyDashboard, is this a hierarchy level dashboard
 * @param {array} dataItems, and array of DataItemPropTypes used to figure out where to put certain data items
 */
export const generateAttributesFromSeriesUpdate = (
  updatedCard: Card,
  oldCard: Card,
  dataItems: Array<DataItemType>,
  isSummaryDashboard: boolean,
  isHierarchyDashboard: boolean
): Card => {
  // clone the card
  let newCard = { ...updatedCard };

  // Do I detect any differences in the attributes that are showing in the card taking the aggregation info list into account
  const [addedAttributes, removedAttributes, updatedAttributes] = determineAttributeChanges(
    oldCard,
    // replaceRawAttributeWithSummaryDashboardAttribute(newCard, aggregationInfoList),
    newCard,
    dataItems
  );

  // Only for summaryDashboard, not combining datafilter and groupBy for non-summary dashboards
  const dataFilterGroupBy = isSummaryDashboard
    ? determineDataFilterGroupBy(addedAttributes, updatedAttributes)
    : [];

  const timeGrainFromAttributes =
    ((!isEmpty(addedAttributes) || !isEmpty(updatedAttributes)) &&
      determineDataSourceTimeGrain(addedAttributes, updatedAttributes)) ||
    newCard?.dataSource?.timeGrain;

  /** Update the dataSource sections to have the new attributes */
  if (newCard.type === CARD_TYPES.TABLE) {
    // do not set dimensions in the attributes section, put them in the groupBy section
    const newAttributeColumns = addedAttributes.filter(column => column.destination !== 'groupBy');
    const removedAttributeColumns = removedAttributes.filter(
      column => column.destination !== 'groupBy'
    );
    const updatedAttributeColumns = updatedAttributes.filter(
      column => column.destination !== 'groupBy'
    );

    const attributes = determineDataSourceAttributes(
      newCard,
      newAttributeColumns,
      removedAttributeColumns,
      updatedAttributeColumns
    );

    newCard = {
      ...newCard,
      dataSource: {
        ...newCard.dataSource,
        attributes: attributes.filter(
          attribute => attribute.attribute !== 'timestamp' && attribute.id !== 'timestamp' // TODO: can remove once PAL sets dataItemId on timestamp
        ), // the timestamp should be excluded from the dataSource section
      },
    };

    // group by is updated at the PAL layer

    let { dataSource } = newCard;
    // Need to update the timeGrain if a new timeRange is selected or if there is no range
    if (newCard?.timeRange !== oldCard?.timeRange || !newCard?.dataSource?.range) {
      const timestampColumn = newCard.content?.columns?.find(
        column => column.dataItemId === 'timestamp' || column.dataSourceId === 'timestamp' // TODO: can remove once fixed by the PAL
      );
      const updatedDataSource = determineDataSourceBasedOnSelectedTimeRange(
        newCard?.dataSource.timeGrain || timestampColumn ? TIME_GRAINS.DAY : null,
        newCard?.timeRange,
        newCard.type
      );
      dataSource = {
        ...newCard.dataSource,
        // if our attributes have a timeGrain set, they should override the default
        ...omit(updatedDataSource, ...(timeGrainFromAttributes ? ['timeGrain'] : [])),
      };
    }
    newCard = {
      ...newCard,
      dataSource: {
        ...dataSource,
        // if they explicitly set the timeGrain in their attribute, then use it rather than the default
        ...(timeGrainFromAttributes ? { timeGrain: timeGrainFromAttributes } : {}),
      },
    };
  }

  // BAR CHARTS and TIME SERIES CARDS
  if (newCard.type === CARD_TYPES.TIMESERIES || newCard.type === CARD_TYPES.BAR) {
    // if we add a new dataItem to the card we need to generate a dataSource attribute for it

    // Find the dataItem being added
    const dataSourceAttributes = determineDataSourceAttributes(
      newCard,
      addedAttributes,
      removedAttributes,
      updatedAttributes
    );
    newCard = {
      ...newCard,
      dataSource: {
        ...newCard.dataSource,
        attributes: dataSourceAttributes,
        // if our attributes have a timeGrain set, they should override the default
        ...omit(
          determineDataSourceBasedOnSelectedTimeRange(
            newCard?.dataSource?.timeGrain || TIME_GRAINS.DAY,
            newCard?.timeRange,
            newCard.type
          ),
          ...(timeGrainFromAttributes ? ['timeGrain'] : [])
        ),
        // if our attributes have a time grain set we should override the default time Grain
        ...(timeGrainFromAttributes ? { timeGrain: timeGrainFromAttributes } : {}),
      },
    };
  }

  // VALUE CARDS
  // if we add a new dataItem to the value card we need to generate a dataSource attribute for it
  if (newCard.type === CARD_TYPES.VALUE) {
    // Find the dataItem being added
    const dataSourceAttributes = determineDataSourceAttributes(
      newCard,
      addedAttributes,
      removedAttributes,
      updatedAttributes
    );

    // Set the card data
    newCard = {
      ...newCard,
      dataSource: {
        ...newCard.dataSource,
        attributes: dataSourceAttributes,
      },
    };
  }

  // IMAGE CARD
  // Check to see if the hotspots attributes have changed and regenerate the attributes in the datasource if so
  if (updatedCard?.type === CARD_TYPES.IMAGE) {
    // Default deviceType/device dashboards to include 'deviceid' as a groupby
    const groupBy = isHierarchyDashboard ? [] : ['deviceid'];
    // Find the dataItem being added
    const newAttributes = determineDataSourceAttributes(
      newCard,
      addedAttributes,
      removedAttributes,
      updatedAttributes
    );

    // Non-dimensional attributes go in the dataSource
    const dataSourceAttributes = newAttributes.filter(
      attribute =>
        // if it's not there in the dataItems section it's being added to the aggregation Info List
        !dataItems.find(dataItem => dataItem.name === attribute.attribute) ||
        dataItems.find(
          dataItem =>
            dataItem.name === attribute.attribute && dataItem.type !== DATAITEM_TYPE_DIMENSION
        )
    );

    // go through each of the new attributes that ARE dimensions and add them to the groupBy
    newAttributes.forEach(attribute => {
      if (
        dataItems.find(
          dataItem =>
            dataItem.name === attribute.attribute && dataItem.type === DATAITEM_TYPE_DIMENSION
        )
      ) {
        // dimensions are used to group by in the summary dashboard for images
        if (isSummaryDashboard) {
          groupBy.push(attribute.attribute);
        } else if (
          !dataSourceAttributes.find(dataSourceAttribute => dataSourceAttribute.id === attribute.id)
        ) {
          // but dimensions go in the attributes section for instance dashboards
          dataSourceAttributes.push(attribute);
        }
      }
    });

    newCard = {
      ...newCard,
      dataSource: {
        ...omit(newCard.dataSource, 'attributes'),
        // add dataSource.attributes only if dataSourceAttributes is not empty
        ...(!isEmpty(dataSourceAttributes) ? { attributes: dataSourceAttributes } : {}),
        // if we have any dynamic hotspots or dimensional values we need add the group by section
        ...(some(newCard?.values?.hotspots, { type: HotspotRenderType.DYNAMIC }) ||
        groupBy?.length > 1
          ? { groupBy }
          : {}),
      },
    };
  }

  return omit(
    {
      ...newCard,
      ...(newCard.dataSource
        ? {
            dataSource: {
              ...omit(
                newCard.dataSource,
                // if the data source doesn't have any aggregator, omit it's timegrain
                ...(every(newCard?.dataSource?.attributes, attribute => !attribute.aggregator) ||
                !isTimeBasedCard(newCard)
                  ? ['timeGrain']
                  : [])
              ),
              // If the JSON Editor passes us an interval, we need to move it to the dataSource timeGrain
              ...(newCard.interval ? { timeGrain: newCard.interval } : {}),
              // Check to see if we need the groupBy specified for a dataFilter
              ...(!isEmpty(newCard.dataSource?.groupBy) || !isEmpty(dataFilterGroupBy)
                ? { groupBy: uniq((newCard.dataSource?.groupBy || []).concat(dataFilterGroupBy)) }
                : {}),
            },
          }
        : {}),
    },
    'interval'
  );
};

/**
 * Determines which time ranges are valid for a card
 * @param {object} card
 * @param {array} selectedDataItems
 * @param {array} dataItems
 * @returns {array} array of valid time ranges
 */
export const getValidTimeRanges = (card: Card, i18n: Record<string, string>): string[] => {
  const validRanges = determineValidTimeRangesForCard({
    ...card,
    isExpanded: false,
    i18n,
  });
  return Object.keys(validRanges);
};

/**
 * Function calculates the merged summaryDto if this dashboard shares a dto with an existing one
 * @param {*} newDashboardDto after an add summary this is the current DTO set
 * @param {*} matchingDashboardDto this is a previous summary DTO that matches this one on granularity
 * @return {object} mergedSummaryDto the merged dashboard
 */
export const mergeMatchingDashboardsSummaryDto = (
  newDashboardDto: SummaryDto,
  matchingDashboardDto: SummaryDto
): SummaryDto => {
  if (isEmpty(matchingDashboardDto)) {
    return newDashboardDto;
  }
  const mergedDto = {
    ...matchingDashboardDto,
    granularityName: newDashboardDto.granularityName, // the new dashboard name must be used
  };
  // First check for any new aggregators on existing data items and add then to the existing aggregationInfoList aggregMethods
  mergedDto.aggregationInfoList = mergedDto?.aggregationInfoList.map(
    ({ inputDataItem, aggregMethods, ...others }) => {
      // what data item in the new dashboard matches the previous one
      const newAggregatedDataItem = newDashboardDto?.aggregationInfoList.find(
        ({ inputDataItem: newInputDataItem }) => newInputDataItem === inputDataItem
      );

      // if we found a matching item in the new dashboard merge the two aggregMethods together with the previous aggregated data item names winning
      if (newAggregatedDataItem) {
        return {
          ...others,
          inputDataItem,
          aggregMethods: { ...newAggregatedDataItem.aggregMethods, ...aggregMethods },
        };
      }
      // if there's not an matching data item in the new aggregationInfoList, just use this one
      return { ...others, inputDataItem, aggregMethods };
    }
  );
  // finally see if there are any new data items to add to the aggregationInfoList
  mergedDto.aggregationInfoList = mergedDto.aggregationInfoList.concat(
    newDashboardDto?.aggregationInfoList.reduce(
      (mergedAggregationInfoList, newAggregatedDataItem) => {
        const { inputDataItem } = newAggregatedDataItem;
        // First check to see if this data item has been aggregated previously
        const matchingDashboardAggregatedDataItem = mergedDto.aggregationInfoList.find(
          ({ inputDataItem: previousInputDataItem }) => previousInputDataItem === inputDataItem
        );
        // If it hasn't just use the one from the new summary dto
        if (!matchingDashboardAggregatedDataItem) {
          mergedAggregationInfoList.push(newAggregatedDataItem);
        }
        return mergedAggregationInfoList;
      },
      []
    )
  );

  return mergedDto;
};

/**
 * Build a dashboard template based on the selected dataItems
 * @param {string} summaryName string to use when generating the data items
 * @param {string} timeFrequency
 * @param {Array<Object>} aggregationInfoList
 * @param {Array<Object>} dataItemsArg
 * @param {string} timeLabel translated x-axis time label
 * @returns {Object}
 */
export const createDefaultTemplate = (
  summaryName: string,
  timeFrequency: string,
  aggregationInfoList: Array<AggregationInfo>,
  dataItemsArg: DataItemType[],
  timeLabel: string
): DashboardTemplate => {
  const dataItems = mergeAggregationInfoListAndDataItems(aggregationInfoList, dataItemsArg);
  const timeGrain = mapTimeFrequencyToTimeGrain(timeFrequency);
  return {
    title: summaryName,
    timeGrain,
    cards: dataItems
      .map(dataItem => {
        const { name, columnType, aggregMethods } = dataItem;
        const aggregationFunctions = sortBy(Object.keys(aggregMethods), value => value);
        const dataSourceAttributes = aggregationFunctions.map(aggregator => ({
          aggregator,
          attribute: aggregMethods[aggregator], // use the saved name
          id: aggregMethods[aggregator],
        }));

        // non-graphable metrics need a value card
        if (
          columnType === COLUMNTYPE_LITERAL ||
          (columnType === COLUMNTYPE_BOOLEAN && !isDataItemAlert(dataItem))
        ) {
          // string metric, return value card
          return {
            id: `card-${name}-value`,
            dataSource: {
              attributes: dataSourceAttributes,
            },
            content: {
              attributes: aggregationFunctions.map(aggregator => ({
                dataSourceId: aggregMethods[aggregator],
                label: capitalize(`${aggregator} ${name}`),
              })),
            },
            size:
              aggregationFunctions.length <= 2
                ? CARD_SIZES.SMALL
                : aggregationFunctions.length <= 3
                ? CARD_SIZES.MEDIUM
                : CARD_SIZES.LARGETHIN,
            title: `${name}`,
            type: CardTypes.VALUE,
          } as Card;
        }

        const range =
          timeGrain === TIME_GRAINS.HOUR
            ? { count: -24, interval: TIME_GRAINS.HOUR }
            : timeGrain === TIME_GRAINS.DAY
            ? { count: -7, interval: TIME_GRAINS.DAY }
            : timeGrain === TIME_GRAINS.WEEK
            ? { count: -4, interval: TIME_GRAINS.WEEK }
            : timeGrain === TIME_GRAINS.MONTH
            ? { count: -12, interval: TIME_GRAINS.MONTH }
            : { count: -5, interval: TIME_GRAINS.DAY };

        if (isDataItemMetric(dataItem) && isDataItemNumeric(dataItem)) {
          // numerical metric, return line chart card
          return {
            id: `card-${name}-timeseries`,
            dataSource: {
              attributes: dataSourceAttributes,
              range,
              timeGrain,
            },
            content: {
              series: aggregationFunctions.map(aggregator => ({
                dataSourceId: aggregMethods[aggregator],
                label: capitalize(aggregator),
              })),
              xLabel: timeLabel,
              yLabel: capitalize(name),
            },
            size: CARD_SIZES.MEDIUM,
            title: name,
            type: CardTypes.TIMESERIES,
          } as Card;
        }

        if (isDataItemAlert(dataItem)) {
          // alerts should use bar chart
          return {
            id: `card-${name}-barchart`,
            dataSource: {
              attributes: dataSourceAttributes,
              range,
              timeGrain,
            },
            content: {
              // should only have 1 'count'
              series: aggregationFunctions.map(aggregator => ({
                dataSourceId: aggregMethods[aggregator],
                label: capitalize(aggregator),
              })),
              xLabel: timeLabel,
              // This will need to change if theres every more than 1 aggregator on alert cards
              yLabel: 'Count',
              type: BAR_CHART_TYPES.SIMPLE,
              timeDataSourceId: 'timestamp',
            },
            size: CARD_SIZES.MEDIUM,
            title: name,
            type: CardTypes.BAR,
          } as Card;
        }

        return undefined;
      })
      .filter(i => i),
  };
};

/**
 * @param {Object} summaryConfig
 * @param {Object} summary
 * @returns {Boolean}
 */
export const hasContentsChanged = (summaryConfig: SummaryDto, summary: SummaryDto): boolean =>
  !isEqual(
    // Have to sort because isEqual will not return properly with arrays of nested objects that are out of order with each other
    sortBy(summaryConfig.aggregationInfoList, 'inputDataItem'),
    sortBy(summary.aggregationInfoList, 'inputDataItem')
  );

/**
 * @param {Object} summaryConfig
 * @param {Object} summary
 * @returns {Boolean}
 */
export const hasDimensionsChanged = (summaryConfig: SummaryDto, summary: SummaryDto): boolean =>
  !isEqual(
    // Have to sort because isEqual will not return properly with arrays of nested objects that are out of order with each other
    summaryConfig?.dimensionNames?.sort(),
    summary?.dimensionNames?.sort()
  );

/**
 * This adds the list of newly selected aggregators to the data items list so that validation still works
 * @param {Array<Object>} aggregationInfoList
 * @param {Array<Object>} dataItems
 * @returns {Array<Object>}
 */
export const addNewAggregationListToDataItems = (
  aggregationInfoList: AggregationInfo[],
  dataItems: DataItemType[]
): DataItemType[] =>
  dataItems && aggregationInfoList
    ? dataItems.concat(
        aggregationInfoList.reduce((acc, { inputDataItem, aggregMethods }) => {
          const matchingDataItem = dataItems.find(dataItem => dataItem.name === inputDataItem);
          // For each aggregator, generate as a dataItem for validation
          Object.values(aggregMethods).forEach((newName: string) => {
            // If the item doesn't already exist
            if (!dataItems.find(dataItem => dataItem.name === newName)) {
              // All of these generated data items become CALCULATED_METRICS
              acc.push({
                ...{
                  ...matchingDataItem,
                  type: DATAITEM_TYPE_CALCULATED_METRIC,
                  tags: [DATAITEM_TAG_COMPLEX],
                  // count data items become numbers just by their nature, regardless of what type they are
                  columnType:
                    newName.includes('count') || !matchingDataItem?.columnType
                      ? COLUMNTYPE_NUMBER
                      : matchingDataItem.columnType,
                },
                name: newName,
              });
            }
          });
          return acc;
        }, [])
      )
    : dataItems;

/**
 * Check to see if the user has modified their dashboard template from the default generated one
 * @param {Object} summaryDto the metadata about the summary
 * @param {Object} currentDashboard the existing dashboard
 * @param {Array<Object>} dataItems
 * @param {string} timeLabel label to use in the card
 * @returns {Boolean}
 */
export const hasDashboardTemplateBeenModified = (
  summaryDto: SummaryDto,
  currentDashboard: DashboardTemplate,
  dataItems: DataItemType[],
  timeLabel: string
): boolean => {
  if (!summaryDto || !summaryDto.granularityName || isEmpty(currentDashboard?.cards)) {
    return false;
  }

  const defaultTemplate = omit(
    createDefaultTemplate(
      summaryDto.granularityName,
      summaryDto.granularityTimeFrequency,
      summaryDto.aggregationInfoList,
      dataItems,
      timeLabel
    ),
    ['layouts']
  );
  // Does the current dashboard still match the default generated one?
  return !isEqual(defaultTemplate, omit(currentDashboard, ['layouts']));
};

/**
 * Maps over an array of function names for a data item to generate an dictionary of the new Data Item names
 * selectedFunctions is an array of function names
 * @param {Array<string>} selectedFunctions  the aggregation function name. min, max, last, first .etc
 * @param {string} dataItemId the name of the data item
 * @param {string} summaryName the name of the summary dashboarad
 */
export const mapAggregationFunctions = (
  selectedFunctions: Array<string>,
  dataItemId: string,
  summaryName: string,
  oldAggregMethods?: AggregationMethods
): AggregationMethods =>
  selectedFunctions.reduce((acc, functionName) => {
    acc[functionName] =
      oldAggregMethods && oldAggregMethods[functionName]
        ? oldAggregMethods[functionName]
        : `${dataItemId}_${summaryName}_${functionName}`;
    return acc;
  }, {});

/**
 * @param {string} name
 * @param {Object} intlProp
 * @param {Object} summaries
 */
export const validateUniqueNameField = (
  name: string,
  formatMessage: FormatMessageType,
  summaries: Array<SummaryDto>
): string | undefined =>
  validateUniqueName(name, summaries, 'granularityName', {
    intl: { formatMessage },
  });

/**
 * @param {string} summaryName
 * @param {Object} formatMessage
 * @param {Object} currentSummaries
 * @param {Boolean} isEdit
 * @param {string} entityTypeName
 */
export const validateSummaryName = (
  summaryName: string,
  formatMessage: FormatMessageType,
  currentSummaries: Array<SummaryDto>,
  isEdit: boolean,
  entityTypeName: string
): string | undefined => {
  const intlProp = {
    intl: { formatMessage },
  };

  // TODO: please verify this name max length code. Backend is not using summary name for DM_table creation.
  // Summary name used in grain table creation. So we need to modify the length accoringly.
  // i.e maxLength = Default database table name length - (dm_ + entityTypeName + Summary Name / Grain name)
  const dBMaxTableNameLength = getLocalStorageIsDB2()
    ? DB2_IDENTIFIER_MAXLENGTH
    : POSTGRESQL_IDENTIFIER_MAXLENGTH;
  const entityTypeNameLength = entityTypeName?.length || 0;
  const maxLength = dBMaxTableNameLength - entityTypeNameLength - 3;

  const summaryNameError =
    validateMaxLength(summaryName, intlProp, maxLength) ||
    validateNoSpaces(summaryName, intlProp) ||
    validateSpecialCharacters(summaryName, intlProp);
  if (summaryNameError) {
    return summaryNameError;
  }

  // only check for unique name if this is the first time the dashboard is being created
  if (!isEdit) {
    return validateUniqueNameField(summaryName, formatMessage, currentSummaries);
  }

  return undefined;
};

/**
 * All fields must have values with no empty strings.
 * Summary name must pass full validation and there must be at least 1 dimension
 * @param summaryDto
 * @param formatMessage
 * @param i18NMessages
 * @param summaries
 * @param isEdit
 * @param entityTypeName
 * @returns
 */
export const validateDefinitionForm = (
  summaryDto: SummaryDto,
  formatMessage: FormatMessageType,
  i18NMessages: Record<string, string>,
  summaries: Array<SummaryDto>,
  isEdit: boolean,
  entityTypeName: string
): Record<string, string> => {
  // typescript to allow later assignment
  const errors = {} as Record<string, string>;
  const granularityNameValidation = validateSummaryName(
    summaryDto.granularityName,
    formatMessage,
    summaries,
    isEdit,
    entityTypeName
  );
  if (granularityNameValidation) {
    errors.granularityName = granularityNameValidation;
  }
  if (summaryDto.granularityName.length === 0) {
    errors.granularityName = formatMessage(i18NMessages.required);
  }
  if (summaryDto.granularityTimeFrequency.length === 0) {
    errors.granularityTimeFrequency = formatMessage(i18NMessages.required);
  }

  return errors;
};

/**
 * Update the summary configuration and dashboard with the values from the Definition page
 * @param {Object} formValues
 * @param {Object} summaryDto summary dashboard details
 * @param {Function} setSummaryConfig
 */
export const handleDefinitionChange = (
  formValues: SummaryDto,
  summaryDto: SummaryDto,
  setSummaryConfig: Function
): void => {
  const { granularityName, granularityTimeFrequency } = formValues;
  if (
    !isEqual(
      // only regenerate the config if we're different
      { granularityName, granularityTimeFrequency },
      pick(summaryDto, ['granularityName', 'granularityTimeFrequency'])
    )
  ) {
    // If the summary name changes we need to regenerate the aggregationList
    const aggregationInfoList =
      granularityName === summaryDto.granularityName
        ? summaryDto.aggregationInfoList
        : summaryDto.aggregationInfoList.map(({ aggregMethods, inputDataItem, ...others }) => ({
            ...others,
            inputDataItem,
            aggregMethods: mapAggregationFunctions(
              Object.keys(aggregMethods),
              inputDataItem,
              granularityName
            ),
          }));

    setSummaryConfig(summaryConfig => ({
      ...summaryConfig,
      aggregationInfoList,
      granularityName,
      granularityTimeFrequency,
    }));
  }
};

/**
 * Update the config and dashboard with the values from the Contents Page
 * @param {Array<Object>} selectedDataItems
 * @param {Object} summaryDto, the current summaryDto from the summary dashboard
 * @param {Object} currentDashboard, the current dashboard
 * @param {Array<Object>} validDataItemsForSummary a list of valid data items
 * @param {string} formattedTimeMessage the x-axis label
 * @param {func} setSummaryDto a function to use to update the summary dto
 * @param {func} setDashboard a callback function to use to update the dashboard template accordingly
 */
export const handleContentsChange = (
  selectedDataItems,
  summaryDto: SummaryDto,
  currentDashboard: DashboardTemplate,
  validDataItemsForSummary: DataItemType[],
  formattedTimeMessage: string,
  setSummaryDto: Function,
  setDashboard: Function
): void => {
  const { selectedDataItems: aggregationInfoList } = selectedDataItems;
  // Only recreate the default template if the selected data items are different
  // If the current dashboard matches the default, we can regenerate it, otherwise leave it alone!
  if (
    !isEqual(aggregationInfoList, summaryDto.aggregationInfoList) &&
    !hasDashboardTemplateBeenModified(
      summaryDto,
      currentDashboard,
      validDataItemsForSummary,
      formattedTimeMessage
    )
  ) {
    const newDashboard = createDefaultTemplate(
      summaryDto.granularityName,
      summaryDto.granularityTimeFrequency,
      aggregationInfoList,
      validDataItemsForSummary,
      formattedTimeMessage
    );
    setDashboard(newDashboard);
  }

  setSummaryDto({
    ...summaryDto,
    aggregationInfoList: aggregationInfoList.map(i => ({
      inputDataItem: i.inputDataItem,
      aggregMethods: i.aggregMethods,
      catalogFunction: 'AggregateItems',
    })),
  });
};

export const handleContentsValidation = (
  summaryConfig: SummaryDto,
  setError: Function,
  formattedSelectDataItem: string,
  formattedSelectAggregationMethod: string
): boolean => {
  const { aggregationInfoList } = summaryConfig;
  if (aggregationInfoList.length === 0) {
    setError(formattedSelectDataItem);
    return false;
  }
  if (some(aggregationInfoList, dataItem => isEmpty(dataItem.aggregMethods))) {
    setError(formattedSelectAggregationMethod);
    return false;
  }
  return true;
};

/**
 * Checks the template to see if we need to add data to the cards for rendering in the template editor
 * @param {Object} template
 * @returns {Boolean}
 */
export const shouldTemplateLoadData = (template: DashboardTemplate): boolean =>
  template?.cards?.some(
    card =>
      // Do we need to setup some alert card
      (card.type === CUSTOM_CARD_TYPES.ALERT && card.hideHeader !== true) ||
      // do we need to reload some images
      (card.type === CARD_TYPES.IMAGE && card?.content?.id && !card?.content?.src) || // do we need to readd the dynamic thresholds to timeseries card
      (card.type === CARD_TYPES.TIMESERIES &&
        (!card.renderEditContent || // do we need to add the custom threshold editor
          (isEmpty(card?.content?.thresholds) &&
            !isEmpty(card?.dataSource?.additionalData?.dataFilter))))
  );

/**
 * This is to map our JSON format for imagecard back to what they expect in the DashboardEditor
 * @param {*} card
 * @param images
 */
export const transformOurJSONToImageCard = (card: Card, images: Array<ImageType>): Card => {
  if (card.type === CARD_TYPES.IMAGE && isEmpty(card.values?.hotspots)) {
    // first clone the card
    let updatedCard = { ...card };

    // load image from the backend and store it in the src attribute
    if (!updatedCard.content.src && images) {
      updatedCard = {
        ...updatedCard,
        content: {
          ...updatedCard.content,
          src: images.find(image => updatedCard.content.id === image.id)?.src,
        },
      };
    }

    // map our legacy image if it's set to the id
    if (!updatedCard.content.id && updatedCard.content.image) {
      updatedCard.content.id = updatedCard.content.image;
      delete updatedCard.content.image;
    }

    // need to map the hotspots into the correct location
    updatedCard.values = {};
    updatedCard.values.hotspots =
      card.content?.hotspots?.map(hotspot => ({
        ...omit(hotspot, 'locations'),
        type:
          hotspot.type ||
          (hotspot.locations[0]?.xDataSourceId
            ? HotspotRenderType.DYNAMIC
            : HotspotRenderType.FIXED),
        x: !isNil(hotspot.locations[0].x)
          ? hotspot.locations[0].x
          : hotspot.locations[0].xDataSourceId,
        y: !isNil(hotspot.locations[0].y)
          ? hotspot.locations[0].y
          : hotspot.locations[0].yDataSourceId,
      })) || [];
    return omit(updatedCard, 'content.hotspots');
  }
  return card;
};

/**
 * Adds the columns state to the alert card for new ones (and legacy ones that have not been migrated)
 * @param card
 * @param dimensions
 * @param formatMessage
 * @pram isHierarchyDashboard to determine if hierarchy resource
 * @returns
 */
export const initializeAlertCardColumnState = (
  card: Card,
  dimensions: DataItemType[],
  formatMessage: FormatMessageType,
  isHierarchyDashboard = false
): Card => ({
  ...card,
  columnsState:
    card.columnsState ||
    getAlertCardDefaultColumsState(formatMessage, isHierarchyDashboard).concat(
      // Sort the non-default alert fields alphabetically
      sortBy(
        [
          ...(dimensions?.map(dimension => ({
            id: dimension.name,
            columnName: dimension.name,
            isVisible: false,
          })) || []),
        ],
        ({ columnName }) => columnName.toLowerCase()
      )
    ),
});

/**
 * Takes our alert card format and converts it to the PAL format of a CUSTOM card,
 * adding the custom render function
 * @param {*} card
 */
export const reformatAlertCardToPAL = (
  card: Card,
  accessControls: Array<string>,
  isHierarchyDashboard = false
): Card => ({
  ...card,
  content: (
    size,
    { cardToolbar, title: cardTitle, description, customRowCount, customRowHeight, columnsState } // cardConfig
  ): JSX.Element => (
    <AlertsTable
      title={cardTitle}
      tooltip={description}
      key={JSON.stringify(size)} // this is a hack because if they change the size, I need it to recalculate the items per page
      isPreview
      customToolbarContent={cardToolbar}
      hasShortRows={customRowHeight === 'short'}
      numberOfRowsPerPage={customRowCount}
      dashboardEditorColumns={columnsState}
      size={size}
      hasTableViewManagement={false}
      testId={`alerts-table-card-content-${cardTitle}`}
      accessControls={accessControls}
      isHierarchyDashboard={isHierarchyDashboard}
    />
  ),
  type: CardTypes.ALERT,
  hideHeader: true,
});

/**
 * Takes our third party card format and converts it to the PAL format of a CUSTOM card,
 * adding the custom render function
 * @param {*} card
 */
export const reformatThirdPartyCardToPAL = (
  card: Card,
  hierarchyPath: string,
  hierarchyType: string
): Card => ({
  ...card,
  content: (size, { title, id, size: cardSize, customContent }): JSX.Element => (
    <ThirdPartyCardContainer
      hierarchyPath={hierarchyPath}
      hierarchyType={hierarchyType}
      title={title}
      size={size}
      cardSize={cardSize}
      id={id}
      isPreview
      customContent={customContent}
    />
  ),
  type: CardTypes.THIRD_PARTY,
});

/**
 *
 * @param {*} card details is there a card that needs thresholds
 * @param {Array} an array of all the thresholds that have been loaded into the dashboard editor
 * @returns card with thresholds added
 */
export const updateThresholdsInTimeSeriesCard = (
  card: Card,
  thresholds: Array<Threshold>
): Card => {
  // Do I need to add thresholds?
  if (!isEmpty(thresholds) && !isEmpty(card?.dataSource?.additionalData?.dataFilter)) {
    return {
      ...card,
      content: {
        ...card.content,
        // Since the thresholds array is for the whole template, we need to find and pass only the thresholds necessary for this card
        thresholds: thresholds.filter(
          threshold => card?.dataSource?.additionalData?.dataFilter?.name?.includes(threshold.label)
        ),
      },
    };
  }
  // Do I need to clear thresholds?
  if (isEmpty(card?.dataSource?.additionalData?.dataFilter)) {
    return {
      ...card,
      content: omit(card.content, 'thresholds'),
    };
  }
  return card;
};

/**
 *
 * @param {*} onChange callback for when the card should change based on editor changes
 * @param {*} cardConfig custom card settings
 * @param {func} formatMessage callback to translate a message
 * @param {array} alertDataItems set of alert data items
 */
export const renderTimeSeriesAlertContent = (
  onChange: CardOnChangeType,
  cardConfig: Card,
  formatMessage: FormatMessageType,
  alertDataItems: DataItemType[]
): RenderEditContentReturnType[] => [
  {
    header: {
      title: formatMessage(messages.alerts),
      tooltip: { tooltipText: formatMessage(messages.alertSectionTooltip) },
    },
    content: (
      <TimeSeriesCardEditContent
        cardConfig={cardConfig}
        onChange={onChange}
        alertDataItems={alertDataItems}
      />
    ),
  },
];

/**
 * Certain custom editor panels are required for the alert card edit form
 * @param card
 * @param formatMessage
 * @param entityId
 * @returns
 */
export const addCustomEditorToAlertCard = (
  card: Card,
  formatMessage: FormatMessageType,
  entityId: string
): Card => ({
  ...card,
  renderEditContent: (
    onChange: CardOnChangeType,
    cardConfig: Card
  ): RenderEditContentReturnType[] => [
    {
      header: {
        title: formatMessage(messages.columns),
        tooltip: {
          tooltipText: formatMessage(messages.alertCardContentTooltip),
        },
      },
      content: <AlertCardEditContent onChange={onChange} cardConfig={cardConfig} />,
    },
  ],
  renderEditSettings: (onChange: CardOnChangeType, cardConfig: Card): JSX.Element => (
    <AlertCardEditSettings
      onChange={onChange}
      cardConfig={cardConfig}
      isSummaryDashboard={!entityId}
    />
  ),
});

/**
 * Certain custom editor panels are required for the third party card edit form
 * @param card
 * @param formatMessage
 * @param entityId
 * @returns
 */
export const addCustomEditorToThirdPartyCard = (
  card: Card,
  formatMessage: FormatMessageType,
  dataItems: DataItemType[],
  entityTypeName: string,
  entityId: string,
  docURL: string
): Card => ({
  ...card,
  renderEditContent: (
    onChange: CardOnChangeType,
    cardConfig: Card
  ): RenderEditContentReturnType[] => [
    {
      content: (
        <ThirdPartyCardEditContent
          onChange={onChange}
          cardConfig={cardConfig}
          dataItems={dataItems}
          entityTypeName={entityTypeName}
          entityId={entityId}
          docURL={docURL}
          key={`${cardConfig.id}-third-party-card-edit-content`}
        />
      ),
    },
  ],
});

/**
 * Time series cards need additional edit panels to support adding alert markup to the lines, this function adds them
 * @param card
 * @param formatMessage
 * @param dataItems
 * @returns
 */
export const addCustomAlertEditorToTimeSeriesCard = (
  card: Card,
  formatMessage: FormatMessageType,
  dataItems: DataItemType[]
): Card => ({
  ...card,
  renderEditContent: (
    onChange: CardOnChangeType,
    cardConfig: Card
  ): RenderEditContentReturnType[] =>
    // don't render this section until they select some data for their lines
    !isEmpty(cardConfig?.content?.series)
      ? renderTimeSeriesAlertContent(
          onChange,
          cardConfig,
          formatMessage,
          dataItems.filter(
            dataItem => isDataItemAlert(dataItem) || dataItem.tags.includes(DATAITEM_TAG_ALERT)
          )
        )
      : null,
});

/**
 * Table Cards need additional edit capabilities in the DashboardEditor to select non-default datatypes, this adds them
 * @param card
 * @param formatMessage
 * @param entityTypes
 * @param selectedDataType
 * @returns
 */
export const addCustomDataTypeSelectorToTableCard = (
  card: Card,
  entityTypes: EntityTypesType,
  entityTypeName: string
): Card => ({
  ...card,
  renderEditContent: (
    onChange: CardOnChangeType,
    cardConfig: Card
  ): RenderEditContentReturnType[] => [
    {
      content: (
        <CustomTableCardEditContent
          onChange={onChange}
          card={cardConfig}
          entityTypes={entityTypes}
          selectedDataType={
            card?.dataSource?.type && card?.dataSource?.type !== DASHBOARD_DATA_TYPES.ALERT
              ? card.dataSource.type
              : entityTypeName
          }
        />
      ),
    },
  ],
});

/**
 * This is to reduce the fontSize of ValueCard if the default fontSiz is being used
 * @param card
 * @param fontSize
 */
export const changeValueCardFontSize = (card: Card, fontSize: number): Card => {
  if (!card.hasOwnProperty('fontSize')) {
    return { ...card, fontSize };
  }
  return card;
};

/**
 * DashboardEditor needs to show custom card editing panels for certain types of card, this adds those panels
 *
 * @param {Object} card
 * @returns {Object} card with custom config / data
 */
export const setupEditorDataForCard = (
  card: Card,
  entityTypeName: string,
  entityId: string,
  hierarchyType: string,
  formatMessage: FormatMessageType,
  dataItems: DataItemType[],
  entityTypes: EntityTypesType, // all entitytypes that are available in the system,
  images: Array<ImageType>,
  fontSize: number,
  docURL: string
): Card => {
  const isHierarchyDashboard =
    hierarchyType !== RESOURCE_TYPES.DEVICE && hierarchyType !== RESOURCE_TYPES.DEVICE_TYPE;

  switch (card.type) {
    case CARD_TYPES.TIMESERIES:
      return addCustomAlertEditorToTimeSeriesCard(card, formatMessage, dataItems);
    case CARD_TYPES.TABLE:
      return isHierarchyDashboard
        ? card
        : addCustomDataTypeSelectorToTableCard(card, entityTypes, entityTypeName);
    case CUSTOM_CARD_TYPES.ALERT:
      return addCustomEditorToAlertCard(
        initializeAlertCardColumnState(
          card,
          dataItems.filter(dataItem => isDataItemDimension(dataItem)),
          formatMessage,
          isHierarchyDashboard
        ),
        formatMessage,
        entityId
      );
    case CUSTOM_CARD_TYPES.THIRD_PARTY:
      return addCustomEditorToThirdPartyCard(
        card,
        formatMessage,
        dataItems,
        entityTypeName,
        entityId,
        docURL
      );
    case CARD_TYPES.IMAGE:
      return transformOurJSONToImageCard(card, images);
    // REMOVE WHEN ISSUE IS COMPLETED: https://github.com/carbon-design-system/carbon-addons-iot-react/issues/3090#issue-1065517421
    case CARD_TYPES.VALUE:
      return changeValueCardFontSize(card, fontSize);
    default:
      return card;
  }
};

/**
 * Convert our card to renderable in dashboard editor
 *
 * @param card
 * @param entityTypeId
 * @param entityId
 * @param i18n
 * @param dimensions
 * @param formatMessage
 * @param dataItems
 * @param thresholds
 * @param entityTypes
 * @param images
 * @returns
 */
export const reformatOurJSONToPALFormat = (
  card: Card,
  i18n: Record<string, string>, // need to attach this to image cards
  dimensions: DataItemType[],
  formatMessage: FormatMessageType,
  thresholds: Array<Threshold>, // valid thresholds for the timeseriescard
  images: Array<ImageType>, // all available images
  hierarchyPath: string,
  hierarchyType: string,
  accessControls: Array<string>
): Card => {
  const isHierarchyDashboard =
    hierarchyType !== RESOURCE_TYPES.DEVICE && hierarchyType !== RESOURCE_TYPES.DEVICE_TYPE;

  if (card.type === CUSTOM_CARD_TYPES.ALERT) {
    return reformatAlertCardToPAL(card, accessControls, isHierarchyDashboard);
  }
  if (card.type === CUSTOM_CARD_TYPES.THIRD_PARTY) {
    return reformatThirdPartyCardToPAL(card, hierarchyPath, hierarchyType);
  }
  if (card.type === CARD_TYPES.IMAGE) {
    return { ...transformOurJSONToImageCard(card, images), i18n }; // TODO: migrate the special i18n for the ImageCard through the DashboardEditor
  }
  if (card.type === CARD_TYPES.TIMESERIES) {
    return updateThresholdsInTimeSeriesCard(card, thresholds);
  }
  return card;
};

/**
 * Unfortunately the imagecard stores it's hotspots in a different location than us, as they change it
 * we need to update the JSON accordingly
 * @param {} card
 */
export const transformImageCardToOurJSON = (card: Card, itemsToOmit: string[]): Card => {
  const fieldsToOmit = [
    'values',
    'children',
    'locale',
    'isResizable',
    'dashboardBreakpoints',
    'cardDimensions',
    'dashboardColumns',
    'rowHeight',
    'className',
  ];
  // when we change any hotspot initially the value will be stored in card.values.hotspots and this gets moved into card.content.hotspots location
  if (!isEmpty(card.values?.hotspots)) {
    const updatedCard = { ...card }; // need to map the hotspots into the correct location

    // First handle fixed and text hotspots
    updatedCard.content.hotspots =
      card.values?.hotspots
        ?.filter(
          hotspot =>
            hotspot.type === HotspotRenderType.FIXED ||
            hotspot.type === HotspotRenderType.TEXT ||
            // check for legacy hotspots without type set
            (!hotspot.type && !hotspot.locations?.find(location => location.xDataSourceId))
        )
        .map(hotspot => ({
          ...omit(hotspot, 'x', 'y'),
          content: {
            ...hotspot.content,
            attributes: hotspot.content?.attributes?.map(attribute => omit(attribute, itemsToOmit)),
          },
          type: hotspot.type,
          locations:
            // new format has x and y directly on hotspot
            [{ ...pick(hotspot, 'x', 'y') }],
        })) || [];

    // Then need to map dynamic hotspots to our special keys
    updatedCard.content.hotspots = updatedCard.content.hotspots.concat(
      card.values?.hotspots
        ?.filter(
          hotspot =>
            hotspot.type === HotspotRenderType.DYNAMIC || // new hotspots will have type set
            hotspot.locations?.find(location => location.xDataSourceId)
        )
        .map(hotspot => ({
          ...omit(hotspot, 'x', 'y'),
          content: {
            ...hotspot.content,
            attributes: hotspot.content?.attributes?.map(attribute => omit(attribute, itemsToOmit)),
          },
          type: hotspot.type,
          locations: [
            {
              xDataSourceId: hotspot.x,
              yDataSourceId: hotspot.y,
            },
          ],
        })) || []
    );
    return omit(updatedCard, fieldsToOmit) as Card; // children has react elements and the card gets stringifies in addons
  }
  if (card.dataSource && isEmpty(card.dataSource)) {
    fieldsToOmit.push('dataSource');
  }
  return omit(card, fieldsToOmit) as Card; // children has react elements and the card gets stringifies in addons
};

/**
 * AlertCard needs to get his card type reset to ALERT
 * @param {Object} card
 * @returns {Object} card with custom config / data
 */
export const reformatPALCardToOurJSON = (card: Card): Card => {
  const itemsToOmit = [
    'aggregationMethod',
    'aggregationMethods',
    'grain',
    'dataItemId',
    'type',
    'children', // children has react elements and the card gets stringifies in addons
    'hasStreamingMetricEnabled',
    'kpiFunctionDto',
    'uuid',
    'columnType',
    'dataItemType',
    'eventName',
  ];
  const timeSeriesItemsToOmit = ['thresholds'];

  switch (card.type) {
    case CUSTOM_CARD_TYPES.THIRD_PARTY:
      return {
        ...omit(card, 'children'), // children has react elements and the card gets stringifies in addons
      };
    // support fake data for our custom alerts card
    case CARD_TYPES.CUSTOM:
    case CUSTOM_CARD_TYPES.ALERT:
      return {
        ...omit(card, 'hideHeader', 'children'), // children has react elements and the card gets stringifies in addons
        type: CardTypes.ALERT,
        dataSource: { ...card.dataSource, type: DASHBOARD_DATA_TYPES.ALERT },
      };
    case CARD_TYPES.IMAGE:
      return transformImageCardToOurJSON(card, itemsToOmit);
    case CARD_TYPES.BAR:
    case CARD_TYPES.TIMESERIES:
      // if we are a time based barChart with a category sub-groupBy the label will be
      // unnecessary and will throw a validation error
      if (
        card.type === CARD_TYPES.BAR &&
        card.content?.timeDataSourceId === 'timestamp' &&
        card.content?.categoryDataSourceId
      ) {
        itemsToOmit.push('label');
      }
      if (card.type === CARD_TYPES.TIMESERIES && isNaN(card?.content.maximumDataPoints)) {
        timeSeriesItemsToOmit.push('maximumDataPoints');
      }
      return {
        ...omit(card, 'renderEditContent'), // custom render function is dynamic
        content: {
          ...omit(card.content, timeSeriesItemsToOmit), // thresholds are dynamic
          series: card.content?.series?.map(item => omit(item, itemsToOmit)),
        },
      };
    case CARD_TYPES.VALUE:
      return {
        ...card,
        content: {
          ...card.content,
          attributes: card.content?.attributes?.map(item => omit(item, itemsToOmit)),
        },
      };
    case CARD_TYPES.TABLE:
      return {
        ...omit(card, 'renderEditContent'),
        content: {
          ...card.content,
          columns: card.content?.columns?.map(item =>
            omit(
              item,
              itemsToOmit.filter(itemToOmit => itemToOmit !== 'type')
            )
          ),
        },
      };
    default:
      return card;
  }
};

/**
 * verifies the initial dashboard doesn't match the updated one
 * @param {Object} originalTemplate
 * @param {Object} updatedDashboard
 * @returns {Boolean}
 */
export const isTemplateChanged = (
  originalTemplate: DashboardTemplate | DashboardTemplateType,
  updatedDashboard: DashboardTemplate | DashboardTemplateType
): boolean => !isEqual(originalTemplate, updatedDashboard);

/**
 * converts a dataurl to file that can be uploaded
 * dataurl
 */
export const dataURLToFile = (dataurl, filename: string): File => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  // eslint-disable-next-line no-plusplus
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const migrateCardSizes = (card: Card): Card => {
  const updatedCard = { ...card };
  switch (updatedCard.size) {
    case 'XSMALL':
      updatedCard.size = CARD_SIZES.SMALL;
      break;
    case 'XSMALLWIDE':
      updatedCard.size = CARD_SIZES.SMALLWIDE;
      break;
    case 'TALL':
      updatedCard.size = CARD_SIZES.LARGETHIN;
      break;
    case 'WIDE':
      updatedCard.size = CARD_SIZES.MEDIUMWIDE;
      break;
    case 'XLARGE':
      updatedCard.size = CARD_SIZES.LARGEWIDE;
      break;
    default:
  }
  return updatedCard;
};

/** Function that strips internal indicators from cards */
export const stripInternalFromCards = (template: DashboardTemplate): DashboardTemplate => ({
  ...omit(template, '_id', '_rev', 'entitytype_id', 'tenantId'),
  cards: Array.isArray(template.cards)
    ? template.cards.map(card => {
        let updatedCard = reformatPALCardToOurJSON(card);
        updatedCard = migrateCardSizes(updatedCard);
        return omit(
          updatedCard,
          'setupError',
          'values',
          'error',
          'isLoading',
          'children', // need to remove the react component for draggable handle
          'content.imgState', // for image cards we don't want this
          'content.src', // for image cards we don't want to export this/or store in our database
          'i18n' // needed for image cards to render in the editor, but not part of our schema
        ) as Card;
      })
    : [],
});

/**
 * Need to convert the catalog function name to dashboard config constants
 * example: Minimum -> min, StandardDeviation -> std,
 * @param name name of the catalog function
 * @returns
 */
const convertAggregatorCatalogFunctionName = (name: string): string => {
  switch (name) {
    case 'Mean':
      return 'mean';
    case 'Minimum':
      return 'min';
    case 'Maximum':
      return 'max';
    case 'Sum':
      return 'sum';
    case 'First':
      return 'first';
    case 'Last':
      return 'last';
    case 'Count':
      return 'count';
    case 'StandardDeviation':
      return 'std';
    default:
      return name;
  }
};

/**
 * Function to get V2 aggregation methods to show
 * @param dataItemKPIs
 * @param formatMessage
 * @returns
 */
export const getV2AggregationMethodsForDataItem = (
  dataItemKPIs: Array<KPIFunctionDtoType>,
  formatMessage: FormatMessageType
): { id: string; text: string }[] => {
  const availableAggregationMethods = [
    {
      id: 'mean',
      text: formatMessage(messages.mean),
    },

    {
      id: 'min',
      text: formatMessage(messages.min),
    },

    {
      id: 'max',
      text: formatMessage(messages.max),
    },

    {
      id: 'sum',
      text: formatMessage(messages.sum),
    },

    {
      id: 'first',
      text: formatMessage(messages.first),
    },

    {
      id: 'last',
      text: formatMessage(messages.last),
    },

    {
      id: 'count',
      text: formatMessage(messages.count),
    },

    {
      id: 'std',
      text: formatMessage(messages.std),
    },
  ];

  const aggregationMethodsToShow = [{ id: 'none', text: formatMessage(messages.none) }];

  // Setting value to true if corresponding kpiFunction exists
  dataItemKPIs.forEach(dataItemKPI => {
    const { catalogFunctionDto } = dataItemKPI;
    if (catalogFunctionDto.version === Version.V2) {
      const matchingAggregationMethod = availableAggregationMethods.find(
        availableAggregationMethod =>
          availableAggregationMethod.id ===
          convertAggregatorCatalogFunctionName(catalogFunctionDto.name)
      );

      if (matchingAggregationMethod) {
        // if there are already any aggregations then do not add into the show list
        const matchingAggregationMethodToShow = aggregationMethodsToShow.find(
          aggregationMethodToShow => aggregationMethodToShow.id === matchingAggregationMethod.id
        );
        if (!matchingAggregationMethodToShow) {
          aggregationMethodsToShow.push(matchingAggregationMethod);
        }
      }
    }
  });

  return aggregationMethodsToShow;
};

/**
 * Return the true when we are going to show Aggregation dropdown. Only used for V2 streaming data items.
 * @param dataItem Data item
 * @returns {boolean} true or false
 */
export function hasAggregationDropdownForStreamingDataItem(dataItem): boolean {
  return (
    dataItem.dataItemType !== DATAITEM_TYPE_DIMENSION &&
    dataItem.columnType !== COLUMNTYPE_TIMESTAMP &&
    (dataItem.dataItemType === DATAITEM_TYPE_METRIC ||
      (dataItem.dataItemType === DATAITEM_TYPE_DERIVED &&
        (isDataItemCategoryTransformer(dataItem) || isDataItemCategoryAlert(dataItem))))
  );
}
